import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { ERPPage, ERPPageHeader, ERPKPICard, ERPError, ERPCard, ERPFilterSelect, ERPSearchInput, ERPLoading, ERPEmpty } from '../../components/ui/ERPLayout';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const formatCurrency = (v) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })?.format(v || 0);
const formatPct = (v) => `${(v || 0)?.toFixed(1)}%`;
const formatKg = (v) => `${(v || 0)?.toFixed(2)} kg`;
const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; }
};

// ─── DATE HELPERS (local, no UTC shift) ──────────────────────────────────────
const toLocalDateStr = (date) => {
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  let d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getQuickDateRange = (preset) => {
  const now = new Date();
  const today = toLocalDateStr(now);
  switch (preset) {
    case 'today': return { dateFrom: today, dateTo: today };
    case 'week': {
      const dow = now?.getDay() === 0 ? 6 : now?.getDay() - 1;
      const mon = new Date(now); mon?.setDate(now?.getDate() - dow);
      const sun = new Date(mon); sun?.setDate(mon?.getDate() + 6);
      return { dateFrom: toLocalDateStr(mon), dateTo: toLocalDateStr(sun) };
    }
    case 'month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: toLocalDateStr(first), dateTo: toLocalDateStr(last) };
    }
    case 'prev_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { dateFrom: toLocalDateStr(first), dateTo: toLocalDateStr(last) };
    }
    case 'quarter': {
      const q = Math.floor(now?.getMonth() / 3);
      const first = new Date(now.getFullYear(), q * 3, 1);
      const last = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { dateFrom: toLocalDateStr(first), dateTo: toLocalDateStr(last) };
    }
    case 'year': {
      return { dateFrom: `${now?.getFullYear()}-01-01`, dateTo: `${now?.getFullYear()}-12-31` };
    }
    case 'all': return { dateFrom: '', dateTo: '' };
    default: return null;
  }
};

// ─── COSTE CORTADOR según casuística (mano de obra, NO precio al cliente) ────
// NORMAL < 10 kg → 20 €  |  NORMAL >= 10 kg → 25 €
// EMPLATADO → 25 €  |  PALETILLA → 15 €  |  GRANDE_Y_EMPLATADO → 30 €
//
// IMPORTANTE: TUPPER_CON_GRASA NO redefine el coste del cortador.
// El cortador de TUPPER sigue la casuística normal de la pieza (NORMAL/PALETILLA).
// Los 30 € de TUPPER son el PRECIO COBRADO AL CLIENTE (ingreso), no el coste del cortador.
const calcularCosteCortador = (tipo, emplatado, pesoInicial) => {
  const peso = parseFloat(pesoInicial) || 0;
  if (tipo === 'PALETILLA') return 15;
  if (tipo === 'GRANDE_Y_EMPLATADO') return 30;
  // TUPPER_CON_GRASA: el cortador sigue casuística normal (jamón NORMAL o PALETILLA)
  // NO retornar 30 aquí — esos 30 € son el ingreso del servicio, no el coste del cortador
  if (tipo === 'EMPLATADO' || emplatado === true) return 25;
  // NORMAL (incluye TUPPER_CON_GRASA que no es PALETILLA)
  return peso < 10 ? 20 : 25;
};

// ─── CÁLCULO AUTOMÁTICO DE BLÍSTERES ─────────────────────────────────────────
// Para formato 100g: numero_blisteres = peso_aprovechado_kg × 10 (= g/100)
// Coste plástico: 0,07 € por blíster
const COSTE_BLISTERE = 0.07;
const COSTE_MATERIALES_500G = 2.0; // 2 € por pieza en formato 500g

const calcularBlisteres = (pesoAprovechado, formatoSalida) => {
  if (!pesoAprovechado || pesoAprovechado <= 0) return null;
  const fmt = (formatoSalida || '')?.toUpperCase();
  if (fmt === '100G' || fmt === '100') {
    return Math.round(pesoAprovechado * 10);
  }
  return null;
};

const calcularCosteEnvases = (numeroBlisteres, formatoSalida, tipo) => {
  // TUPPER_CON_GRASA: la grasa la coloca el cortador directamente, sin envasado al vacío
  // coste_envasador = 0, no calcular blísteres, no aplicar coste de envasado al vacío
  if (tipo === 'TUPPER_CON_GRASA') return 0;
  const fmt = (formatoSalida || '')?.toUpperCase();
  if (fmt === '500G' || fmt === '500') return COSTE_MATERIALES_500G;
  if (fmt === '100G' || fmt === '100') {
    if (numeroBlisteres != null && numeroBlisteres > 0) {
      return numeroBlisteres * COSTE_BLISTERE;
    }
    return null; // formato 100G pero sin blísteres calculables → null, no 0
  }
  if (fmt === 'EMPLATADO') return null; // emplatado: sin coste de envase automático
  return null; // formato desconocido → null (NO 0)
};

// ─── DETECTAR FORMATO DE SALIDA ───────────────────────────────────────────────
// PRIORIDAD EXACTA:
// 1. loncheados.formato_corte              (guardado directamente en el registro)
// 2. plan_trabajo_piezas.formato_corte     (via plan_trabajo_pieza_id relation)
// 3. plan_trabajo.formato_corte            (via plan_trabajo_id relation)
// 4. Si numero_blisteres > 0 → '100G'
// 5. Si emplatado/tipo EMPLATADO → 'EMPLATADO'
// 6. Si tipo TUPPER_CON_GRASA → 'TUPPER_CON_GRASA'
// 7. FALLBACK HISTÓRICO: si peso_aprovechado > 0 y tipo NORMAL → '100G'
//    (solo para cálculo, NO se escribe en Supabase)
// 8. null / HISTÓRICO SIN FORMATO
const detectarFormatoSalida = (loncheado) => {
  // 1. loncheados.formato_corte (guardado directamente en el registro)
  if (loncheado?.formato_corte) return loncheado?.formato_corte;

  // 2. plan_trabajo_piezas.formato_corte (joined via plan_trabajo_pieza_id)
  const fmtPieza = loncheado?.plan_trabajo_piezas?.formato_corte;
  if (fmtPieza) return fmtPieza;

  // 3. plan_trabajo.formato_corte (joined via plan_trabajo_id)
  const fmtTrabajo = loncheado?.plan_trabajo?.formato_corte;
  if (fmtTrabajo) return fmtTrabajo;

  // 4. Si numero_blisteres > 0 → inferir 100G
  if (loncheado?.numero_blisteres != null && parseInt(loncheado?.numero_blisteres) > 0) return '100G';

  // 5. Si emplatado o tipo EMPLATADO
  if (loncheado?.emplatado === true || loncheado?.tipo === 'EMPLATADO') return 'EMPLATADO';

  // 6. Si tipo TUPPER_CON_GRASA
  if (loncheado?.tipo === 'TUPPER_CON_GRASA') return 'TUPPER_CON_GRASA';

  // 7. FALLBACK HISTÓRICO: registros antiguos sin formato_corte, sin plan de trabajo,
  //    con peso_aprovechado > 0 y tipo NORMAL (o null/sin tipo especial)
  //    → considerarlos 100G para el cálculo histórico de Rentabilidad Sala.
  //    IMPORTANTE: Este fallback NO se escribe en Supabase.
  const tipo = (loncheado?.tipo || '')?.toUpperCase();
  const pesoAprovechado = parseFloat(loncheado?.peso_aprovechado);
  const esCorteNormal = tipo === 'NORMAL' || tipo === '' || tipo === null;
  if (esCorteNormal && !isNaN(pesoAprovechado) && pesoAprovechado > 0) {
    return '100G_HISTORICO'; // Marcado como histórico para distinguirlo en diagnósticos
  }

  // 8. Sin formato conocido
  return null;
};

// ─── CALCULAR INGRESO DEL SERVICIO ────────────────────────────────────────────
// FUENTE ÚNICA: tarifa configurada del cliente según casuística (POR PIEZA, nunca × kg)
// Leída dinámicamente desde public.clientes via JOIN en la consulta Supabase.
// Si se modifica una tarifa en Supabase, Rentabilidad Sala usará automáticamente el nuevo valor.
//
// TUPPER_CON_GRASA: ingreso = 30 € por pieza (precio cobrado al cliente por ese servicio)
//
// PRIORIDAD:
// 1. Para TUPPER_CON_GRASA: 30 € fijo (precio del servicio)
// 2. tarifa del cliente por casuística (tarifa_emplatado / tarifa_sin_emplatado_menor / tarifa_sin_emplatado_mayor)
//    leída desde clientes.tarifa_* — NUNCA multiplicada por kg
// 3. Sin tarifa → null
//
// NO usar: precio_cliente, ldd_rentabilidad_control, precio del producto, peso × precio/kg
const TUPPER_PRECIO_SERVICIO = 30; // 30 € por pieza — precio cobrado al cliente por TUPPER_CON_GRASA

const calcularIngreso = (loncheado, clienteTarifas) => {
  // Prioridad 1: TUPPER_CON_GRASA → 30 € por pieza (precio del servicio, no del cortador)
  if (loncheado?.tipo === 'TUPPER_CON_GRASA') {
    return { ingreso: TUPPER_PRECIO_SERVICIO, fuente: 'tupper_precio_fijo' };
  }

  // Prioridad 2: tarifa configurada en el cliente según casuística
  // Leída dinámicamente desde public.clientes (tarifa_emplatado / tarifa_sin_emplatado_menor / tarifa_sin_emplatado_mayor)
  // Importe POR PIEZA — NUNCA multiplicar por kg
  if (clienteTarifas) {
    const esEmplatado = loncheado?.emplatado === true || loncheado?.tipo === 'EMPLATADO' || loncheado?.tipo === 'GRANDE_Y_EMPLATADO';
    const peso = parseFloat(loncheado?.peso_inicial) || 0;
    let tarifa = null;
    if (esEmplatado && clienteTarifas?.tarifa_emplatado != null) {
      tarifa = parseFloat(clienteTarifas?.tarifa_emplatado);
    } else if (!esEmplatado && peso < 10 && clienteTarifas?.tarifa_sin_emplatado_menor != null) {
      tarifa = parseFloat(clienteTarifas?.tarifa_sin_emplatado_menor);
    } else if (!esEmplatado && peso >= 10 && clienteTarifas?.tarifa_sin_emplatado_mayor != null) {
      tarifa = parseFloat(clienteTarifas?.tarifa_sin_emplatado_mayor);
    }
    if (tarifa != null && !isNaN(tarifa) && tarifa > 0) {
      return { ingreso: tarifa, fuente: 'tarifa_cliente' };
    }
  }

  return { ingreso: null, fuente: null };
};

// ─── RENTABILIDAD BADGE ───────────────────────────────────────────────────────
const RentabilidadBadge = ({ margen, provisional }) => {
  if (margen === null || margen === undefined) return <span className="text-white/30 text-xs">—</span>;
  const suffix = provisional ? ' · Provisional' : '';
  if (margen >= 30) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#3A7F6B]/15 text-[#3A7F6B] border border-[#3A7F6B]/25">🟢 Rentable{suffix}</span>;
  if (margen >= 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C9A24D]/15 text-[#C9A24D] border border-[#C9A24D]/25">🟡 Margen bajo{suffix}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E05252]/15 text-[#E05252] border border-[#E05252]/25">🔴 Pérdida{suffix}</span>;
};

// ─── ESTADO CALCULO BADGE ─────────────────────────────────────────────────────
const EstadoCalculoBadge = ({ estado }) => {
  if (estado === 'COMPLETO') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#3A7F6B]/15 text-[#3A7F6B] border border-[#3A7F6B]/25">✓ Calculada</span>
  );
  if (estado === 'PROVISIONAL') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C9A24D]/15 text-[#C9A24D] border border-[#C9A24D]/25">⚠ Provisional</span>
  );
  if (estado === 'SIN_TARIFA') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E05252]/15 text-[#E05252] border border-[#E05252]/25">Sin tarifa</span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-white/5 text-white/40 border border-white/10">✗ Sin datos</span>
  );
};

// ─── PESO INCONSISTENTE BADGE ─────────────────────────────────────────────────
const PesoInconsistenteBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E05252]/15 text-[#E05252] border border-[#E05252]/25">⚠ Pesos inconsistentes</span>
);

const getMargenColor = (m) => {
  if (m >= 30) return '#3A7F6B';
  if (m >= 0) return '#C9A24D';
  return '#E05252';
};

const CHART_COLORS = ['#D6EF4E', '#3A7F6B', '#C9A24D', '#8097D6', '#E05252', '#60A5FA'];

// ─── SORT ICON ────────────────────────────────────────────────────────────────
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span className="text-white/20 ml-1">↕</span>;
  return <span className="text-[#D6EF4E] ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

// ─── LONCHEADO DETAIL MODAL ───────────────────────────────────────────────────
const LoncheadoDetailModal = ({ loncheado, onClose }) => {
  if (!loncheado) return null;

  const pesoInicial = loncheado?.peso_inicial_calc;
  const pesoAprovechado = loncheado?.peso_aprovechado_calc;
  const merma = (pesoInicial > 0 && pesoAprovechado >= 0) ? pesoInicial - pesoAprovechado : null;
  const rendimiento = loncheado?.rendimiento_calc;
  const numeroBlisteres = loncheado?.numero_blisteres_final;
  const ingreso = loncheado?.ingreso;
  const ingresoFuente = loncheado?.ingreso_fuente;
  let costeCortador = loncheado?.coste_cortador;
  let costeCortadorFuente = loncheado?.coste_cortador_fuente;
  const costeEnvasador = loncheado?.coste_envasador;
  let costeEnvases = loncheado?.coste_envases;
  let costeEnvasesFuente = loncheado?.coste_envases_fuente;
  let costeTotal = loncheado?.coste_total;
  let costeTotalFuente = loncheado?.coste_total_fuente;
  let beneficio = loncheado?.beneficio;
  let margen = loncheado?.margen;
  let estadoCalculo = loncheado?.estado_calculo;
  const pesosInconsistentes = loncheado?.pesos_inconsistentes;
  let rendimientoNoCoincide = loncheado?.rendimiento_no_coincide;
  const rendimientoCalculado = loncheado?.rendimiento_calculado;
  const formatoSalida = loncheado?.formato_salida;
  const blistersCalculados = loncheado?.numero_blisteres_calculado;
  const blistersOriginales = loncheado?.numero_blisteres;
  const esTupper = loncheado?.tipo === 'TUPPER_CON_GRASA';
  const es100G = (formatoSalida || '')?.toUpperCase() === '100G';
  const necesitaEnvasador = !esTupper && estadoCalculo === 'PROVISIONAL';

  const Row = ({ label, value, color = 'rgba(255,255,255,0.85)', bold = false, fuente = null }) => (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div>
        <span className="text-xs font-oranienbaum text-white/50">{label}</span>
        {fuente && <span className="block text-xs font-oranienbaum text-white/25 mt-0.5">Fuente: {fuente}</span>}
      </div>
      <span className={`text-sm font-oranienbaum ${bold ? 'font-bold' : 'font-semibold'}`} style={{ color }}>{value}</span>
    </div>
  );

  const fuenteIngreso = ingresoFuente === 'tupper_precio_fijo' ? 'TUPPER_CON_GRASA — precio fijo del servicio (30 €/pieza)'
    : ingresoFuente === 'tarifa_cliente' ? 'Tarifa dinámica del cliente (public.clientes — tarifa_emplatado / tarifa_sin_emplatado_menor / tarifa_sin_emplatado_mayor)'
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#1A1A1A] rounded-2xl border border-white/12 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
          <div>
            <h2 className="text-lg font-bold font-oranienbaum text-[#D6EF4E]">
              Detalle Económico — {loncheado?.cliente_nombre || 'Loncheado'}
            </h2>
            <p className="text-xs font-oranienbaum text-white/40 mt-0.5">
              {formatDate(loncheado?.fecha_produccion || loncheado?.created_at)} · {loncheado?.producto_nombre || '—'} · {loncheado?.empleado_nombre || '—'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
            <Icon name="X" size={16} className="text-white/60" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <EstadoCalculoBadge estado={estadoCalculo} />
            {pesosInconsistentes && <PesoInconsistenteBadge />}
            {rendimientoNoCoincide && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C9A24D]/15 text-[#C9A24D] border border-[#C9A24D]/25">⚠ Rendimiento no coincide</span>
            )}
            {margen !== null && estadoCalculo !== 'SIN_DATOS' && estadoCalculo !== 'SIN_TARIFA' && !pesosInconsistentes && (
              <RentabilidadBadge margen={margen} provisional={estadoCalculo === 'PROVISIONAL'} />
            )}
            {esTupper && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#8097D6]/15 text-[#8097D6] border border-[#8097D6]/25">🫙 Tupper con grasa</span>
            )}
          </div>

          {/* PRODUCCIÓN */}
          <div>
            <h3 className="text-xs font-oranienbaum font-bold text-white/40 uppercase tracking-wider mb-2">📦 Producción</h3>
            <div className="bg-[#111111] rounded-xl border border-white/6 px-4 py-1">
              <Row label="Tipo de corte" value={loncheado?.tipo || 'Sin registrar'} color={loncheado?.tipo ? undefined : '#6B7280'} />
              <Row label="Formato de salida" value={formatoSalida || 'HISTÓRICO SIN FORMATO'} color={formatoSalida ? '#8097D6' : '#6B7280'}
                fuente={loncheado?.plan_trabajo_piezas?.formato_corte ? 'plan_trabajo_piezas.formato_corte'
                  : loncheado?.plan_trabajo?.formato_corte ? 'plan_trabajo.formato_corte'
                  : formatoSalida ? 'Inferido (blisteres/tipo)'
                  : null} />
              <Row label="Emplatado" value={loncheado?.emplatado ? 'Sí' : 'No'} />
              <Row label="Peso inicial" value={pesoInicial > 0 ? formatKg(pesoInicial) : 'Sin registrar'} color={pesoInicial > 0 ? undefined : '#6B7280'} />
              <Row label="Peso aprovechado" value={pesoAprovechado >= 0 && pesoInicial > 0 ? formatKg(pesoAprovechado) : 'Sin registrar'} color={pesoAprovechado >= 0 && pesoInicial > 0 ? undefined : '#6B7280'} />
              <Row label="Merma" value={merma !== null ? formatKg(merma) : 'Sin registrar'} color={merma !== null ? '#E05252' : '#6B7280'} />
              <Row label="Rendimiento" value={rendimiento !== null ? formatPct(rendimiento) : 'Sin registrar'} color={rendimiento !== null ? getMargenColor(rendimiento) : '#6B7280'} />
              {rendimientoNoCoincide && rendimientoCalculado !== null && (
                <Row label="Rendimiento calculado (pesos)" value={formatPct(rendimientoCalculado)} color="#C9A24D" fuente="peso_aprovechado / peso_inicial × 100 — difiere >2 pp del almacenado" />
              )}
              <Row
                label="Nº blísteres"
                value={numeroBlisteres != null ? `${numeroBlisteres}${blistersCalculados != null && blistersCalculados !== blistersOriginales ? ' (calculado)' : ''}` : esTupper ? 'N/A (Tupper)' : 'Sin registrar'}
                color={numeroBlisteres != null ? '#8097D6' : '#6B7280'}
                fuente={blistersCalculados != null && blistersOriginales != null && blistersOriginales > 0 && blistersCalculados !== blistersOriginales
                  ? `BD: ${blistersOriginales} · Calculado: ${blistersCalculados} (peso_aprovechado × 10)`
                  : blistersCalculados != null && (blistersOriginales == null || blistersOriginales === 0)
                    ? `Calculado automáticamente: peso_aprovechado × 10 = ${blistersCalculados}`
                    : null}
              />
            </div>
          </div>

          {/* INGRESOS */}
          <div>
            <h3 className="text-xs font-oranienbaum font-bold text-white/40 uppercase tracking-wider mb-2">💶 Ingresos del Servicio</h3>
            <div className="bg-[#111111] rounded-xl border border-white/6 px-4 py-1">
              <Row
                label="Importe cobrado por el servicio"
                value={ingreso != null && ingreso > 0 ? formatCurrency(ingreso) : 'Sin tarifa registrada'}
                color={ingreso != null && ingreso > 0 ? '#3A7F6B' : '#E05252'}
                bold
                fuente={fuenteIngreso}
              />
              {esTupper && (
                <div className="py-2">
                  <p className="text-xs font-oranienbaum text-[#8097D6]/70">
                    ℹ TUPPER_CON_GRASA: precio del servicio = 30 €/pieza. El cortador añade la grasa directamente. Sin envasado al vacío. Sin trabajador de envasado.
                  </p>
                </div>
              )}
              {(estadoCalculo === 'SIN_TARIFA' || (!ingreso || ingreso === 0)) && (
                <div className="py-2">
                  <p className="text-xs font-oranienbaum text-[#E05252]/70">
                    ⚠ Sin tarifa de servicio. Verificar: loncheados.precio_cliente o tarifa configurada en el cliente.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* COSTES */}
          <div>
            <h3 className="text-xs font-oranienbaum font-bold text-white/40 uppercase tracking-wider mb-2">💸 Costes del Servicio</h3>
            <div className="bg-[#111111] rounded-xl border border-white/6 px-4 py-1">
              <Row
                label="Coste cortador (mano de obra)"
                value={costeCortador != null && costeCortador > 0 ? formatCurrency(costeCortador) : 'Sin registrar'}
                color={costeCortador != null && costeCortador > 0 ? '#E05252' : '#6B7280'}
                fuente={costeCortadorFuente === 'salarios' ? 'Tabla salarios (SUM importe por loncheado_id)'
                  : costeCortadorFuente === 'calculado_casuistica' ? (esTupper ? 'Casuística normal (TUPPER no redefine coste cortador)' : 'Calculado por casuística (tipo/peso)')
                  : null}
              />
              <Row
                label="Coste envasador"
                value={costeEnvasador != null ? (costeEnvasador === 0 ? '0,00 € (TUPPER — sin envasador)' : formatCurrency(costeEnvasador)) : 'Pendiente de configurar'}
                color={costeEnvasador != null ? '#E05252' : '#6B7280'}
                fuente={esTupper ? 'TUPPER_CON_GRASA: grasa añadida por el cortador, sin segundo trabajador' : 'Coste/hora envasador × tiempo estimado (configurable)'}
              />
              <Row
                label="Coste plásticos / envases"
                value={costeEnvases != null && costeEnvases > 0 ? formatCurrency(costeEnvases) : costeEnvases === 0 ? '0,00 € (Tupper/sin envase)' : 'Sin registrar'}
                color={costeEnvases != null && costeEnvases > 0 ? '#E05252' : '#6B7280'}
                fuente={costeEnvasesFuente === 'calculado_100g' ? `Calculado: ${numeroBlisteres} blísteres × 0,07 €`
                  : costeEnvasesFuente === 'calculado_500g'? 'Formato 500g: 2 € por pieza' : esTupper ?'TUPPER_CON_GRASA: sin envasado al vacío'
                  : null}
              />
              {/* Coste conocido — diferenciado de coste definitivo */}
              <Row
                label={necesitaEnvasador ? 'Coste conocido (cortador + envases)' : 'Coste total'}
                value={costeTotal != null && costeTotal > 0 ? formatCurrency(costeTotal) : 'Sin registrar'}
                color={costeTotal != null && costeTotal > 0 ? '#E05252' : '#6B7280'}
                bold
                fuente={necesitaEnvasador ? 'PROVISIONAL — falta coste envasador' : costeTotalFuente === 'calculado' ? 'Calculado: cortador + envases' : null}
              />
            </div>
          </div>

          {/* RESULTADO */}
          <div>
            <h3 className="text-xs font-oranienbaum font-bold text-white/40 uppercase tracking-wider mb-2">📊 Resultado</h3>
            <div className="bg-[#111111] rounded-xl border border-white/6 px-4 py-1">
              <Row
                label={necesitaEnvasador ? 'Beneficio provisional' : 'Beneficio'}
                value={beneficio != null ? formatCurrency(beneficio) : 'Sin datos suficientes'}
                color={beneficio != null ? (beneficio >= 0 ? '#3A7F6B' : '#E05252') : '#6B7280'}
                bold
              />
              <Row
                label="Margen %"
                value={margen !== null ? formatPct(margen) : 'Sin datos suficientes'}
                color={margen !== null ? getMargenColor(margen) : '#6B7280'}
                bold
              />
              <Row
                label="Estado del cálculo"
                value={estadoCalculo === 'COMPLETO' ? 'Completo' : estadoCalculo === 'PROVISIONAL' ? 'Provisional (envasador pendiente)' : estadoCalculo === 'SIN_TARIFA' ? 'Sin tarifa de servicio' : 'Sin datos'}
                color={estadoCalculo === 'COMPLETO' ? '#3A7F6B' : estadoCalculo === 'PROVISIONAL' ? '#C9A24D' : '#E05252'}
              />
            </div>
          </div>

          {pesosInconsistentes && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#E05252]/8 border border-[#E05252]/20">
              <Icon name="AlertTriangle" size={14} className="text-[#E05252] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-oranienbaum text-[#E05252]/80">
                Los datos de peso de este loncheado son inconsistentes. Este registro se excluye del cálculo de rendimiento global.
              </p>
            </div>
          )}
          {necesitaEnvasador && !pesosInconsistentes && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#C9A24D]/8 border border-[#C9A24D]/20">
              <Icon name="Info" size={14} className="text-[#C9A24D] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-oranienbaum text-[#C9A24D]/80">
                <strong>PROVISIONAL — falta coste envasador.</strong> El beneficio mostrado es ingreso − costes conocidos (cortador + envases). Cuando se configure el coste del envasador, pasará a COMPLETO.
              </p>
            </div>
          )}
          {esTupper && estadoCalculo === 'COMPLETO' && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#8097D6]/8 border border-[#8097D6]/20">
              <Icon name="Info" size={14} className="text-[#8097D6] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-oranienbaum text-[#8097D6]/80">
                TUPPER_CON_GRASA: coste envasador = 0 (el cortador añade la grasa directamente). Cálculo definitivo posible.
              </p>
            </div>
          )}
          {estadoCalculo === 'SIN_TARIFA' && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#E05252]/8 border border-[#E05252]/20">
              <Icon name="AlertCircle" size={14} className="text-[#E05252] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-oranienbaum text-[#E05252]/80">
                No existe tarifa de servicio para este loncheado. Este registro NO se incluye en la facturación total.
              </p>
            </div>
          )}
          {rendimientoNoCoincide && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#C9A24D]/8 border border-[#C9A24D]/20">
              <Icon name="AlertTriangle" size={14} className="text-[#C9A24D] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-oranienbaum text-[#C9A24D]/80">
                El rendimiento almacenado difiere más de 2 puntos porcentuales del calculado a partir de los pesos. Solo auditoría — no se ha modificado ningún dato.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── SORTABLE TH ──────────────────────────────────────────────────────────────
const SortableTh = ({ field, label, sortField, sortDir, onSort }) => (
  <th
    className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-white/80 select-none"
    onClick={() => onSort(field)}
  >
    {label}<SortIcon field={field} sortField={sortField} sortDir={sortDir} />
  </th>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const ClientProfitabilityAnalysis = () => {
  const [viewLevel, setViewLevel] = useState('sala');

  // Data
  const [loncheados, setLoncheados] = useState([]);
  const [clientesTarifas, setClientesTarifas] = useState({});
  const [salariosByLoncheado, setSalariosByLoncheado] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Diagnostic state (for control block)
  const [diagnostico, setDiagnostico] = useState(null);

  // Filter options
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [productos, setProductos] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', clienteId: 'all', empleadoId: 'all',
    productoId: 'all', tipoCorte: 'all', rentabilidad: 'all',
    estadoCalculo: 'all', estadoRendimiento: 'all',
    quickDate: 'all'
  });
  const [search, setSearch] = useState('');

  // Detail modal
  const [selectedLoncheado, setSelectedLoncheado] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Sorting
  const [sortField, setSortField] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');

  // ─── LOAD DATA ────────────────────────────────────────────────────────────
  useEffect(() => { loadAll(); }, []);

  // ─── CHUNKED FETCH HELPER ─────────────────────────────────────────────────
  const fetchAllRows = async (table, selectStr, extraFilters = null, maxChunks = 50) => {
    const CHUNK = 1000;
    let allRows = [];
    let from = 0;
    let chunkIndex = 0;

    while (chunkIndex < maxChunks) {
      let query = supabase
        ?.from(table)
        ?.select(selectStr)
        ?.range(from, from + CHUNK - 1);

      if (extraFilters) query = extraFilters(query);

      const { data, error } = await query;
      if (error) throw error;

      const rows = data || [];
      allRows = allRows?.concat(rows);

      if (rows?.length < CHUNK) break;
      from += CHUNK;
      chunkIndex++;
    }

    return allRows;
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1) Load ALL loncheados — chunked to bypass 1000-row Supabase limit
      // Include plan_trabajo_piezas and plan_trabajo relations for formato_corte
      const lonData = await fetchAllRows(
        'loncheados',
        `id, cliente_id, empleado_id, producto_id,
         precio_cliente, tipo, observaciones, created_at,
         emplatado, coste, peso_inicial, peso_aprovechado,
         merma, rendimiento, tarifa, coste_sala, numero_blisteres,
         fecha_produccion, plan_trabajo_id, plan_trabajo_pieza_id,
         lote_interno, lote_externo, formato_corte,
         clientes:cliente_id(id, nombre, tarifa_emplatado, tarifa_sin_emplatado_menor, tarifa_sin_emplatado_mayor),
         empleados:empleado_id(id, nombre),
         productos:producto_id(id, nombre),
         plan_trabajo_piezas:plan_trabajo_pieza_id(id, formato_corte, tipo_corte),
         plan_trabajo:plan_trabajo_id(id, formato_corte, tipo_corte)`,
        (q) => q?.order('created_at', { ascending: false })
      );

      // 2) Load ALL salarios — chunked, aggregate by loncheado_id
      const salData = await fetchAllRows(
        'salarios',
        'id, empleado_id, loncheado_id, importe, tipo, created_at',
        (q) => q?.not('loncheado_id', 'is', null)
      );

      // Aggregate salarios by loncheado_id (SUM importe per loncheado)
      // This prevents 1:N join duplication
      const salMap = {};
      (salData || [])?.forEach(s => {
        if (s?.loncheado_id) {
          salMap[s?.loncheado_id] = (salMap?.[s?.loncheado_id] || 0) + (parseFloat(s?.importe) || 0);
        }
      });
      setSalariosByLoncheado(salMap);

      // NOTE: ldd_rentabilidad_control is NOT loaded for Sala calculations.
      // It belongs to the commercial profitability of LDD products and must NOT
      // contaminate the service accounting of Sala de Corte.

      setLoncheados(lonData || []);

      // Build clientes tarifas map and filter options
      const clienteMap = {};
      const empleadoMap = {};
      const productoMap = {};
      const tarifasMap = {};
      (lonData || [])?.forEach(l => {
        if (l?.clientes) {
          clienteMap[l?.cliente_id] = l?.clientes;
          tarifasMap[l?.cliente_id] = {
            tarifa_emplatado: l?.clientes?.tarifa_emplatado,
            tarifa_sin_emplatado_menor: l?.clientes?.tarifa_sin_emplatado_menor,
            tarifa_sin_emplatado_mayor: l?.clientes?.tarifa_sin_emplatado_mayor,
          };
        }
        if (l?.empleados) empleadoMap[l?.empleado_id] = l?.empleados;
        if (l?.productos) productoMap[l?.producto_id] = l?.productos;
      });
      setClientes(Object.values(clienteMap));
      setEmpleados(Object.values(empleadoMap));
      setProductos(Object.values(productoMap));
      setClientesTarifas(tarifasMap);
    } catch (err) {
      setError(err?.message || 'Error al cargar datos de rentabilidad');
    } finally {
      setLoading(false);
    }
  };

  // ─── ENRICH LONCHEADOS ────────────────────────────────────────────────────
  // ÚNICA FUENTE DE VERDAD para todos los cálculos económicos.
  // KPIs superiores, tabla detallada y agregación por cliente usan exactamente los mismos datos.
  // ldd_rentabilidad_control NO se usa aquí — pertenece al módulo comercial LDD.
  const enrichedLoncheados = useMemo(() => {
    const result = (loncheados || [])?.map(l => {
      // Leer tarifas directamente del JOIN con public.clientes (siempre dinámico desde Supabase)
      // Si se modifica una tarifa en Supabase, se refleja automáticamente aquí
      const clienteTarifas = l?.clientes ? {
        tarifa_emplatado: l?.clientes?.tarifa_emplatado,
        tarifa_sin_emplatado_menor: l?.clientes?.tarifa_sin_emplatado_menor,
        tarifa_sin_emplatado_mayor: l?.clientes?.tarifa_sin_emplatado_mayor,
      } : (clientesTarifas?.[l?.cliente_id] || null);
      const esTupper = l?.tipo === 'TUPPER_CON_GRASA';

      // ── PRODUCCIÓN — STRICT: only peso_inicial and peso_aprovechado ──
      const pesoInicial = parseFloat(l?.peso_inicial);
      const pesoAprovechado = parseFloat(l?.peso_aprovechado);

      const pesoInicialValido = !isNaN(pesoInicial) && pesoInicial > 0 ? pesoInicial : 0;
      const pesoAprovechadoValido = !isNaN(pesoAprovechado) && pesoAprovechado >= 0 ? pesoAprovechado : null;

      // Detect inconsistent weights
      const pesosInconsistentes =
        pesoInicialValido > 0 &&
        pesoAprovechadoValido !== null &&
        (pesoAprovechadoValido > pesoInicialValido || pesoAprovechadoValido < 0);

      // ── PER-RECORD RENDIMIENTO NORMALIZATION ──
      let rendimiento_calc = null;
      let rendimiento_fuente = null;

      if (l?.rendimiento != null) {
        const rv = parseFloat(l?.rendimiento);
        if (!isNaN(rv)) {
          if (rv >= 0 && rv <= 1) {
            rendimiento_calc = rv * 100;
            rendimiento_fuente = 'stored';
          } else if (rv > 1 && rv <= 100) {
            rendimiento_calc = rv;
            rendimiento_fuente = 'stored';
          }
        }
      }

      let rendimiento_calculado = null;
      if (pesoInicialValido > 0 && pesoAprovechadoValido !== null && !pesosInconsistentes) {
        rendimiento_calculado = (pesoAprovechadoValido / pesoInicialValido) * 100;
      }

      if (rendimiento_calc === null && rendimiento_calculado !== null) {
        rendimiento_calc = rendimiento_calculado;
        rendimiento_fuente = 'calculado';
      }

      if (pesosInconsistentes) rendimiento_calc = null;

      let rendimientoNoCoincide = false;
      if (
        rendimiento_fuente === 'stored' &&
        rendimiento_calculado !== null &&
        rendimiento_calc !== null &&
        Math.abs(rendimiento_calc - rendimiento_calculado) > 2
      ) {
        rendimientoNoCoincide = true;
      }

      // ── FORMATO DE SALIDA ──
      // Prioridad: 1) loncheados.formato_corte → 2) plan_trabajo_piezas → 3) plan_trabajo → fallbacks
      const formatoSalidaRaw = detectarFormatoSalida(l);
      // Normalizar: 100G_HISTORICO se trata como 100G para cálculos
      const formatoSalida = formatoSalidaRaw === '100G_HISTORICO' ? '100G' : formatoSalidaRaw;
      const esHistorico = formatoSalidaRaw === '100G_HISTORICO';

      // Track formato source for diagnostics
      let formatoFuente = null;
      if (l?.formato_corte) formatoFuente = 'loncheados_formato_corte';
      else if (l?.plan_trabajo_piezas?.formato_corte) formatoFuente = 'plan_trabajo_piezas';
      else if (l?.plan_trabajo?.formato_corte) formatoFuente = 'plan_trabajo';
      else if (l?.numero_blisteres != null && parseInt(l?.numero_blisteres) > 0) formatoFuente = 'inferido_blisteres';
      else if (l?.emplatado === true || l?.tipo === 'EMPLATADO') formatoFuente = 'inferido_tipo';
      else if (l?.tipo === 'TUPPER_CON_GRASA') formatoFuente = 'inferido_tipo';
      else if (esHistorico) formatoFuente = 'fallback_historico_100g';

      // ── BLÍSTERES ──
      // Para TUPPER_CON_GRASA: no calcular blísteres (la grasa la coloca el cortador directamente)
      const blistersCalculados = esTupper ? null : calcularBlisteres(pesoAprovechadoValido, formatoSalida);
      const blistersOriginales = l?.numero_blisteres != null ? parseInt(l?.numero_blisteres) : null;
      // Para contabilidad: usar el valor real si existe y > 0, si no el calculado
      const numeroBlisteresFinal = esTupper ? null : (
        (blistersOriginales != null && blistersOriginales > 0)
          ? blistersOriginales
          : blistersCalculados
      );

      // ── INGRESO DEL SERVICIO ──
      // PRINCIPIO FUNDAMENTAL: el ingreso es lo que se cobra por el SERVICIO, no el precio del jamón.
      // TUPPER_CON_GRASA: 30 € es el PRECIO COBRADO AL CLIENTE por ese servicio (no el coste del cortador)
      const { ingreso, fuente: ingresoFuente } = calcularIngreso(l, clienteTarifas);

      // ── COSTE CORTADOR ──
      // Prioridad: 1) salarios reales, 2) casuística
      // TUPPER_CON_GRASA: el cortador sigue casuística normal (NORMAL/PALETILLA)
      // Los 30 € de TUPPER son el INGRESO del servicio, NO el coste del cortador
      let costeCortador = null;
      let costeCortadorFuente = null;
      const salarioSum = salariosByLoncheado?.[l?.id];
      if (salarioSum != null && salarioSum > 0) {
        costeCortador = salarioSum;
        costeCortadorFuente = 'salarios';
      } else {
        // Calcular por casuística como fallback
        // calcularCosteCortador ya NO retorna 30 para TUPPER_CON_GRASA
        const costeCasuistica = calcularCosteCortador(l?.tipo, l?.emplatado, pesoInicialValido);
        if (costeCasuistica > 0) {
          costeCortador = costeCasuistica;
          costeCortadorFuente = 'calculado_casuistica';
        }
      }

      // ── COSTE ENVASADOR ──
      // TUPPER_CON_GRASA: coste_envasador = 0 (el cortador añade la grasa, sin segundo trabajador)
      // Para otros formatos: pendiente de configurar → null
      const costeEnvasador = esTupper ? 0 : null;

      // ── COSTE ENVASES / PLÁSTICOS ──
      // TUPPER_CON_GRASA: no calcular blísteres, no aplicar coste de envasado al vacío
      // Para 100G: numeroBlisteresFinal × 0,07 €
      // Para 500G: 2 € por pieza
      let costeEnvases = null;
      let costeEnvasesFuente = null;

      const costeCalculado = calcularCosteEnvases(numeroBlisteresFinal, formatoSalida, l?.tipo);
      if (costeCalculado != null) {
        costeEnvases = costeCalculado;
        const fmt = (formatoSalida || '')?.toUpperCase();
        costeEnvasesFuente = esTupper ? 'tupper_sin_envase'
          : (fmt === '500G' || fmt === '500') ? 'calculado_500g'
          : 'calculado_100g';
      }

      // ── COSTE TOTAL CONOCIDO ──
      // = coste_cortador + coste_envases (coste_envasador pendiente de configurar para no-tupper)
      // TUPPER: coste_envasador = 0, puede calcularse de forma definitiva
      let costeTotal = null;
      let costeTotalFuente = null;
      const costeCortadorNum = costeCortador || 0;
      const costeEnvasesNum = costeEnvases || 0;
      const costeEnvasadorNum = costeEnvasador || 0;
      const costesConocidos = costeCortadorNum + costeEnvasesNum + costeEnvasadorNum;
      if (costesConocidos > 0) {
        costeTotal = costesConocidos;
        costeTotalFuente = 'calculado';
      }

      // ── BENEFICIO PROVISIONAL ──
      // = ingreso_servicio - costes_conocidos
      let beneficio = null;
      if (ingreso != null && costeTotal != null) {
        beneficio = ingreso - costeTotal;
      } else if (ingreso != null && costeCortador != null) {
        beneficio = ingreso - (costeCortador || 0) - (costeEnvases || 0);
      }

      // ── MARGEN % ──
      let margen = null;
      if (beneficio != null && ingreso != null && ingreso > 0) {
        margen = (beneficio / ingreso) * 100;
      }

      // ── ESTADO CÁLCULO ──
      // TUPPER_CON_GRASA: costeEnvasador = 0 → puede ser COMPLETO si tiene ingreso y cortador
      // Para 100G y otros: PROVISIONAL mientras falta coste_envasador
      let estadoCalculo = 'SIN_DATOS';
      const tieneIngreso = ingreso != null && ingreso > 0;
      const tieneCosteCortador = costeCortador != null;
      const envasadorConfigurado = costeEnvasador != null; // null = pendiente, 0 = configurado (tupper)

      if (!tieneIngreso) {
        estadoCalculo = 'SIN_TARIFA';
      } else if (tieneIngreso && tieneCosteCortador && envasadorConfigurado) {
        estadoCalculo = 'COMPLETO';
      } else if (tieneIngreso && tieneCosteCortador) {
        estadoCalculo = 'PROVISIONAL';
      } else if (tieneIngreso) {
        estadoCalculo = 'PROVISIONAL';
      }

      // ── ESTADO RENDIMIENTO ──
      let estadoRendimiento = 'INCOMPLETO';
      if (pesosInconsistentes) {
        estadoRendimiento = 'INCONSISTENTE';
      } else if (rendimiento_calc !== null) {
        estadoRendimiento = 'CORRECTO';
      }

      const loteInterno = l?.lote_interno || null;
      const loteExterno = l?.lote_externo || null;

      return {
        ...l,
        cliente_nombre: l?.clientes?.nombre || '—',
        empleado_nombre: l?.empleados?.nombre || '—',
        producto_nombre: l?.productos?.nombre || '—',
        // Económicos — ÚNICA FUENTE DE VERDAD (sin ldd_rentabilidad_control)
        ingreso,
        ingreso_fuente: ingresoFuente,
        coste_cortador: costeCortador,
        coste_cortador_fuente: costeCortadorFuente,
        coste_envasador: costeEnvasador,
        coste_envases: costeEnvases,
        coste_envases_fuente: costeEnvasesFuente,
        coste_total: costeTotal,
        coste_total_fuente: costeTotalFuente,
        beneficio,
        margen,
        estado_calculo: estadoCalculo,
        estado_rendimiento: estadoRendimiento,
        // Producción
        peso_inicial_calc: pesoInicialValido,
        peso_aprovechado_calc: pesoAprovechadoValido,
        rendimiento_calc,
        rendimiento_calculado,
        rendimiento_no_coincide: rendimientoNoCoincide,
        pesos_inconsistentes: pesosInconsistentes,
        // Blísteres
        numero_blisteres_calculado: blistersCalculados,
        numero_blisteres_final: numeroBlisteresFinal,
        // Formato (conectado via loncheados.formato_corte, plan_trabajo_piezas y plan_trabajo)
        formato_salida: formatoSalida,
        formato_fuente: formatoFuente,
        es_historico: esHistorico,
        // Lotes
        lote_interno: loteInterno,
        lote_externo: loteExterno,
        // Alias para compatibilidad
        coste_salarial: costeCortador,
        // Flags
        es_tupper: esTupper,
        necesita_envasador: !esTupper && estadoCalculo === 'PROVISIONAL',
      };
    });

    // ── DIAGNÓSTICO CONSOLA ──
    const sumaIngresoSala = result?.reduce((s, l) => s + (l?.ingreso || 0), 0);
    const registrosConTarifa = result?.filter(l => l?.ingreso != null && l?.ingreso > 0)?.length;
    const registrosSinTarifa = result?.filter(l => l?.estado_calculo === 'SIN_TARIFA')?.length;
    const registrosCon100G = result?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '100G')?.length;
    const registrosCon500G = result?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '500G')?.length;
    const registrosTupper = result?.filter(l => l?.es_tupper)?.length;
    const registrosSinFormato = result?.filter(l => !l?.formato_salida)?.length;
    const totalBlisters = result?.reduce((s, l) => s + (l?.numero_blisteres_final || 0), 0);
    const costeEnvasesTotal = result?.reduce((s, l) => s + (l?.coste_envases || 0), 0);

    // precio_cliente stats (solo informativo — ya no se usa como fuente de ingreso)
    const conPrecioCliente = result?.filter(l => {
      const pc = parseFloat(l?.precio_cliente);
      return !isNaN(pc) && pc > 0;
    });
    const preciosCliente = conPrecioCliente?.map(l => parseFloat(l?.precio_cliente));
    const pcMin = preciosCliente?.length > 0 ? Math.min(...preciosCliente) : null;
    const pcMax = preciosCliente?.length > 0 ? Math.max(...preciosCliente) : null;
    const pcMedia = preciosCliente?.length > 0 ? preciosCliente?.reduce((s, v) => s + v, 0) / preciosCliente?.length : null;
    const top20PrecioCliente = [...conPrecioCliente]
      ?.sort((a, b) => parseFloat(b?.precio_cliente) - parseFloat(a?.precio_cliente))
      ?.slice(0, 20);

    // formato source counts
    const formatoDesde_loncheados = result?.filter(l => l?.formato_fuente === 'loncheados_formato_corte')?.length;
    const formatoDesde_pieza = result?.filter(l => l?.formato_fuente === 'plan_trabajo_piezas')?.length;
    const formatoDesde_plan = result?.filter(l => l?.formato_fuente === 'plan_trabajo')?.length;
    const formatoInferido = result?.filter(l => l?.formato_fuente === 'inferido_blisteres' || l?.formato_fuente === 'inferido_tipo')?.length;
    const formatoFallbackHistorico = result?.filter(l => l?.formato_fuente === 'fallback_historico_100g')?.length;
    const conPlanTrabajoPiezaId = result?.filter(l => l?.plan_trabajo_pieza_id != null)?.length;
    const conPlanTrabajoId = result?.filter(l => l?.plan_trabajo_id != null)?.length;

    console.group('🔍 DIAGNÓSTICO RENTABILIDAD SALA');
    console.log('Facturación Sala (tarifa servicio):', sumaIngresoSala?.toFixed(2), '€');
    console.log('Registros con tarifa válida:', registrosConTarifa);
    console.log('Registros SIN tarifa:', registrosSinTarifa);
    console.log('--- FORMATO ---');
    console.log('Formato desde loncheados.formato_corte (nuevo campo):', formatoDesde_loncheados);
    console.log('Formato desde plan_trabajo_piezas:', formatoDesde_pieza);
    console.log('Formato desde plan_trabajo:', formatoDesde_plan);
    console.log('Formato inferido (blisteres/tipo):', formatoInferido);
    console.log('Fallback histórico 100G (registros antiguos sin formato):', formatoFallbackHistorico);
    console.log('Sin formato:', registrosSinFormato);
    console.log('--- TUPPER ---');
    console.log('Registros TUPPER_CON_GRASA:', registrosTupper, '(ingreso 30€/pieza, coste cortador por casuística normal)');
    console.log('--- precio_cliente (informativo, NO usado como fuente de ingreso) ---');
    console.log('Registros con precio_cliente > 0:', conPrecioCliente?.length, '(campo ignorado para facturación — se usan tarifas de public.clientes)');
    console.log('Min:', pcMin, '€ | Max:', pcMax, '€ | Media:', pcMedia?.toFixed(2), '€');
    console.groupEnd();

    setDiagnostico({
      sumaIngresoSala,
      registrosConTarifa,
      registrosSinTarifa,
      registrosCon100G,
      registrosCon500G,
      registrosTupper,
      registrosSinFormato,
      totalBlisters,
      costeEnvasesTotal,
      conPrecioCliente: conPrecioCliente?.length,
      pcMin, pcMax, pcMedia,
      top20PrecioCliente,
      formatoDesde_loncheados,
      formatoDesde_pieza,
      formatoDesde_plan,
      formatoInferido,
      formatoFallbackHistorico,
      conPlanTrabajoPiezaId,
      conPlanTrabajoId,
    });

    return result;
  }, [loncheados, salariosByLoncheado, clientesTarifas]);

  // ─── AUDIT STATS (over full dataset, before filters) ─────────────────────
  const auditStats = useMemo(() => {
    const total = enrichedLoncheados?.length;
    const completos = enrichedLoncheados?.filter(l => l?.estado_calculo === 'COMPLETO')?.length;
    const provisionales = enrichedLoncheados?.filter(l => l?.estado_calculo === 'PROVISIONAL')?.length;
    const sinTarifa = enrichedLoncheados?.filter(l => l?.estado_calculo === 'SIN_TARIFA')?.length;
    const inconsistentes = enrichedLoncheados?.filter(l => l?.pesos_inconsistentes)?.length;
    const rendimientosNoCoincidentes = enrichedLoncheados?.filter(l => l?.rendimiento_no_coincide)?.length;
    const uniqueIds = new Set(enrichedLoncheados?.map(l => l?.id));
    const duplicados = total - uniqueIds?.size;
    const con100G = enrichedLoncheados?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '100G')?.length;
    const con500G = enrichedLoncheados?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '500G')?.length;
    const conTupper = enrichedLoncheados?.filter(l => l?.es_tupper)?.length;
    const sinFormato = enrichedLoncheados?.filter(l => !l?.formato_salida)?.length;
    const conPesoAprovechado = enrichedLoncheados?.filter(l => l?.peso_aprovechado_calc != null && l?.peso_aprovechado_calc > 0)?.length;
    const sinPesoAprovechado = enrichedLoncheados?.filter(l => !l?.peso_aprovechado_calc)?.length;
    const totalBlistersReales = enrichedLoncheados?.reduce((s, l) => s + (l?.numero_blisteres != null && l?.numero_blisteres > 0 ? parseInt(l?.numero_blisteres) : 0), 0);
    const totalBlistersCalculados = enrichedLoncheados?.reduce((s, l) => s + (l?.numero_blisteres_calculado || 0), 0);
    const totalBlistersUsados = enrichedLoncheados?.reduce((s, l) => s + (l?.numero_blisteres_final || 0), 0);
    const costeEnvasesTotal = enrichedLoncheados?.reduce((s, l) => s + (l?.coste_envases || 0), 0);
    const pendienteEnvasador = enrichedLoncheados?.filter(l => l?.necesita_envasador)?.length;
    const conTarifa = enrichedLoncheados?.filter(l => l?.ingreso != null && l?.ingreso > 0)?.length;
    const ingresoMedioPorPieza = conTarifa > 0
      ? enrichedLoncheados?.filter(l => l?.ingreso != null && l?.ingreso > 0)?.reduce((s, l) => s + l?.ingreso, 0) / conTarifa
      : null;
    const conTarifa_valida = conTarifa;
    const sinTarifa_count = sinTarifa;

    return {
      total, completos, provisionales, sinTarifa, inconsistentes,
      rendimientosNoCoincidentes, duplicados,
      con100G, con500G, conTupper, sinFormato,
      conPesoAprovechado, sinPesoAprovechado,
      totalBlistersReales, totalBlistersCalculados, totalBlistersUsados,
      costeEnvasesTotal, pendienteEnvasador,
      conTarifa, ingresoMedioPorPieza,
    };
  }, [enrichedLoncheados]);

  // ─── APPLY FILTERS ───────────────────────────────────────────────────────
  const filteredLoncheados = useMemo(() => {
    let d = enrichedLoncheados || [];

    if (filters?.dateFrom) {
      d = d?.filter(l => {
        const fecha = (l?.fecha_produccion || l?.created_at || '')?.split('T')?.[0];
        return fecha >= filters?.dateFrom;
      });
    }
    if (filters?.dateTo) {
      d = d?.filter(l => {
        const fecha = (l?.fecha_produccion || l?.created_at || '')?.split('T')?.[0];
        return fecha <= filters?.dateTo;
      });
    }

    if (filters?.clienteId !== 'all') d = d?.filter(l => l?.cliente_id === filters?.clienteId);
    if (filters?.empleadoId !== 'all') d = d?.filter(l => l?.empleado_id === filters?.empleadoId);
    if (filters?.productoId !== 'all') d = d?.filter(l => l?.producto_id === filters?.productoId);
    if (filters?.tipoCorte !== 'all') d = d?.filter(l => l?.tipo === filters?.tipoCorte);

    if (filters?.rentabilidad === 'rentable') d = d?.filter(l => l?.margen !== null && l?.margen >= 30);
    if (filters?.rentabilidad === 'margen_bajo') d = d?.filter(l => l?.margen !== null && l?.margen >= 0 && l?.margen < 30);
    if (filters?.rentabilidad === 'perdida') d = d?.filter(l => l?.margen !== null && l?.margen < 0);
    if (filters?.rentabilidad === 'sin_tarifa') d = d?.filter(l => l?.estado_calculo === 'SIN_TARIFA');

    if (filters?.estadoCalculo === 'completo') d = d?.filter(l => l?.estado_calculo === 'COMPLETO');
    if (filters?.estadoCalculo === 'provisional') d = d?.filter(l => l?.estado_calculo === 'PROVISIONAL');
    if (filters?.estadoCalculo === 'sin_tarifa') d = d?.filter(l => l?.estado_calculo === 'SIN_TARIFA');
    if (filters?.estadoCalculo === 'sin_datos') d = d?.filter(l => l?.estado_calculo === 'SIN_DATOS');

    if (filters?.estadoRendimiento === 'correcto') d = d?.filter(l => l?.estado_rendimiento === 'CORRECTO');
    if (filters?.estadoRendimiento === 'incompleto') d = d?.filter(l => l?.estado_rendimiento === 'INCOMPLETO');
    if (filters?.estadoRendimiento === 'inconsistente') d = d?.filter(l => l?.estado_rendimiento === 'INCONSISTENTE');

    if (search?.trim()) {
      const q = search?.trim()?.toLowerCase();
      d = d?.filter(l =>
        l?.cliente_nombre?.toLowerCase()?.includes(q) ||
        l?.empleado_nombre?.toLowerCase()?.includes(q) ||
        l?.producto_nombre?.toLowerCase()?.includes(q) ||
        (l?.lote_interno || '')?.toLowerCase()?.includes(q) ||
        (l?.lote_externo || '')?.toLowerCase()?.includes(q) ||
        l?.id?.toString()?.toLowerCase()?.includes(q) ||
        (l?.tipo || '')?.toLowerCase()?.includes(q) ||
        (l?.formato_salida || '')?.toLowerCase()?.includes(q)
      );
    }

    return d;
  }, [enrichedLoncheados, filters, search]);

  // ─── SORTING ──────────────────────────────────────────────────────────────
  const sortedLoncheados = useMemo(() => {
    if (!sortField) return filteredLoncheados;
    return [...filteredLoncheados]?.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'fecha': va = (a?.fecha_produccion || a?.created_at) || ''; vb = (b?.fecha_produccion || b?.created_at) || ''; break;
        case 'cliente': va = a?.cliente_nombre || ''; vb = b?.cliente_nombre || ''; break;
        case 'peso_inicial': va = a?.peso_inicial_calc || 0; vb = b?.peso_inicial_calc || 0; break;
        case 'peso_aprovechado': va = a?.peso_aprovechado_calc ?? -1; vb = b?.peso_aprovechado_calc ?? -1; break;
        case 'rendimiento': va = a?.rendimiento_calc ?? -1; vb = b?.rendimiento_calc ?? -1; break;
        case 'ingreso': va = a?.ingreso ?? -1; vb = b?.ingreso ?? -1; break;
        case 'coste_total': va = a?.coste_total ?? -1; vb = b?.coste_total ?? -1; break;
        case 'beneficio': va = a?.beneficio ?? -Infinity; vb = b?.beneficio ?? -Infinity; break;
        case 'margen': va = a?.margen ?? -Infinity; vb = b?.margen ?? -Infinity; break;
        default: va = ''; vb = '';
      }
      if (typeof va === 'string') {
        return sortDir === 'asc' ? va?.localeCompare(vb) : vb?.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [filteredLoncheados, sortField, sortDir]);

  const handleSort = useCallback((field) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return field;
      }
      setSortDir('asc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  // ─── SALA-LEVEL KPIs ──────────────────────────────────────────────────────
  const salaKPIs = useMemo(() => {
    let d = filteredLoncheados;
    const nPiezas = d?.length;

    const kgIniciales = d?.reduce((s, l) => s + (l?.peso_inicial_calc > 0 ? l?.peso_inicial_calc : 0), 0);

    const kgAprovechados = d?.reduce((s, l) => {
      if (l?.pesos_inconsistentes) return s;
      if (l?.peso_aprovechado_calc === null || l?.peso_aprovechado_calc === undefined) return s;
      return s + l?.peso_aprovechado_calc;
    }, 0);

    const validForRendimiento = d?.filter(l =>
      !l?.pesos_inconsistentes &&
      l?.peso_inicial_calc > 0 &&
      l?.peso_aprovechado_calc !== null &&
      l?.peso_aprovechado_calc >= 0
    );
    const sumInicialValido = validForRendimiento?.reduce((s, l) => s + l?.peso_inicial_calc, 0);
    const sumAprovechadoValido = validForRendimiento?.reduce((s, l) => s + l?.peso_aprovechado_calc, 0);
    const rendimientoMedio = sumInicialValido > 0 ? (sumAprovechadoValido / sumInicialValido) * 100 : null;
    const nSinRendimiento = d?.length - validForRendimiento?.length;

    const facturacion = d?.reduce((s, l) => s + (l?.estado_calculo !== 'SIN_TARIFA' && l?.ingreso ? l?.ingreso : 0), 0);
    const costeCortadores = d?.reduce((s, l) => s + (l?.coste_cortador || 0), 0);
    let costeEnvases = d?.reduce((s, l) => s + (l?.coste_envases || 0), 0);
    const costeEnvasador = d?.reduce((s, l) => s + (l?.coste_envasador || 0), 0);
    let costeTotal = d?.reduce((s, l) => s + (l?.coste_total || 0), 0);
    let beneficio = d?.reduce((s, l) => s + (l?.estado_calculo !== 'SIN_TARIFA' && l?.beneficio != null ? l?.beneficio : 0), 0);
    let margen = facturacion > 0 ? (beneficio / facturacion) * 100 : null;

    const nProvisionales = d?.filter(l => l?.estado_calculo === 'PROVISIONAL')?.length;
    const nSinTarifa = d?.filter(l => l?.estado_calculo === 'SIN_TARIFA')?.length;
    const nSinDatos = d?.filter(l => l?.estado_calculo === 'SIN_DATOS')?.length;
    const nInconsistentes = d?.filter(l => l?.pesos_inconsistentes)?.length;
    const nTupper = d?.filter(l => l?.es_tupper)?.length;
    const nCon100G = d?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '100G')?.length;
    const nSinFormato = d?.filter(l => !l?.formato_salida)?.length;
    const totalBlistersReales = d?.reduce((s, l) => s + (l?.numero_blisteres != null && l?.numero_blisteres > 0 ? parseInt(l?.numero_blisteres) : 0), 0);
    const totalBlistersCalculados = d?.reduce((s, l) => s + (l?.numero_blisteres_calculado || 0), 0);
    const totalBlistersUsados = d?.reduce((s, l) => s + (l?.numero_blisteres_final || 0), 0);
    const nConTarifa = d?.filter(l => l?.ingreso != null && l?.ingreso > 0)?.length;
    const ingresoMedio = nConTarifa > 0 ? facturacion / nConTarifa : null;
    const nPendienteEnvasador = d?.filter(l => l?.necesita_envasador)?.length;

    return {
      nPiezas, kgIniciales, kgAprovechados, rendimientoMedio, nSinRendimiento,
      facturacion, costeCortadores, costeEnvases, costeEnvasador, costeTotal, beneficio, margen,
      nProvisionales, nSinTarifa, nSinDatos, nInconsistentes,
      nTupper, nCon100G, nSinFormato,
      totalBlistersReales, totalBlistersCalculados, totalBlistersUsados,
      nConTarifa, ingresoMedio, nPendienteEnvasador,
    };
  }, [filteredLoncheados]);

  // ─── CLIENT-LEVEL AGGREGATION ─────────────────────────────────────────────
  const clienteData = useMemo(() => {
    const map = {};
    filteredLoncheados?.forEach(l => {
      const id = l?.cliente_id || 'sin_cliente';
      if (!map?.[id]) {
        map[id] = {
          clienteId: id, clienteNombre: l?.cliente_nombre,
          nPiezas: 0, kgIniciales: 0, kgAprovechados: 0,
          facturacion: 0, costeCortadores: 0, costeEnvases: 0,
          costeTotal: 0, beneficio: 0, nProvisionales: 0, nSinTarifa: 0,
        };
      }
      map[id].nPiezas += 1;
      map[id].kgIniciales += l?.peso_inicial_calc > 0 ? l?.peso_inicial_calc : 0;
      if (!l?.pesos_inconsistentes && l?.peso_aprovechado_calc !== null) {
        map[id].kgAprovechados += l?.peso_aprovechado_calc;
      }
      if (l?.estado_calculo !== 'SIN_TARIFA' && l?.ingreso) {
        map[id].facturacion += l?.ingreso;
      }
      map[id].costeCortadores += l?.coste_cortador || 0;
      map[id].costeEnvases += l?.coste_envases || 0;
      map[id].costeTotal += l?.coste_total || 0;
      if (l?.estado_calculo !== 'SIN_TARIFA' && l?.beneficio != null) {
        map[id].beneficio += l?.beneficio;
      }
      if (l?.estado_calculo === 'PROVISIONAL') map[id].nProvisionales += 1;
      if (l?.estado_calculo === 'SIN_TARIFA') map[id].nSinTarifa += 1;
    });

    return Object.values(map)?.map(c => ({
      ...c,
      rendimientoMedio: c?.kgIniciales > 0 ? (c?.kgAprovechados / c?.kgIniciales) * 100 : null,
      margen: c?.facturacion > 0 ? (c?.beneficio / c?.facturacion) * 100 : null,
    }))?.sort((a, b) => (b?.beneficio || 0) - (a?.beneficio || 0));
  }, [filteredLoncheados]);

  // ─── RECONCILIATION TOTALS ────────────────────────────────────────────────
  // Verify DETALLE = CLIENTES = SALA (all from same source)
  const reconciliacion = useMemo(() => {
    const detalleIngreso = filteredLoncheados?.reduce((s, l) => s + (l?.estado_calculo !== 'SIN_TARIFA' && l?.ingreso ? l?.ingreso : 0), 0);
    const detalleCortador = filteredLoncheados?.reduce((s, l) => s + (l?.coste_cortador || 0), 0);
    const detalleEnvases = filteredLoncheados?.reduce((s, l) => s + (l?.coste_envases || 0), 0);
    const detalleCosteConocido = filteredLoncheados?.reduce((s, l) => s + (l?.coste_total || 0), 0);
    const detalleBeneficio = filteredLoncheados?.reduce((s, l) => s + (l?.estado_calculo !== 'SIN_TARIFA' && l?.beneficio != null ? l?.beneficio : 0), 0);

    const clientesIngreso = clienteData?.reduce((s, c) => s + (c?.facturacion || 0), 0);
    const clientesCortador = clienteData?.reduce((s, c) => s + (c?.costeCortadores || 0), 0);
    const clientesEnvases = clienteData?.reduce((s, c) => s + (c?.costeEnvases || 0), 0);
    const clientesCosteConocido = clienteData?.reduce((s, c) => s + (c?.costeTotal || 0), 0);
    const clientesBeneficio = clienteData?.reduce((s, c) => s + (c?.beneficio || 0), 0);

    const salaIngreso = salaKPIs?.facturacion || 0;
    const salaCortador = salaKPIs?.costeCortadores || 0;
    const salaEnvases = salaKPIs?.costeEnvases || 0;
    const salaCosteConocido = salaKPIs?.costeTotal || 0;
    const salaBeneficio = salaKPIs?.beneficio || 0;

    return {
      detalle: { ingreso: detalleIngreso, cortador: detalleCortador, envases: detalleEnvases, costeConocido: detalleCosteConocido, beneficio: detalleBeneficio },
      clientes: { ingreso: clientesIngreso, cortador: clientesCortador, envases: clientesEnvases, costeConocido: clientesCosteConocido, beneficio: clientesBeneficio },
      sala: { ingreso: salaIngreso, cortador: salaCortador, envases: salaEnvases, costeConocido: salaCosteConocido, beneficio: salaBeneficio },
      diff_detalle_clientes: {
        ingreso: Math.abs(detalleIngreso - clientesIngreso),
        cortador: Math.abs(detalleCortador - clientesCortador),
        envases: Math.abs(detalleEnvases - clientesEnvases),
        costeConocido: Math.abs(detalleCosteConocido - clientesCosteConocido),
        beneficio: Math.abs(detalleBeneficio - clientesBeneficio),
      },
      diff_clientes_sala: {
        ingreso: Math.abs(clientesIngreso - salaIngreso),
        cortador: Math.abs(clientesCortador - salaCortador),
        envases: Math.abs(clientesEnvases - salaEnvases),
        costeConocido: Math.abs(clientesCosteConocido - salaCosteConocido),
        beneficio: Math.abs(clientesBeneficio - salaBeneficio),
      },
    };
  }, [filteredLoncheados, clienteData, salaKPIs]);

  // ─── PAGINATION ───────────────────────────────────────────────────────────
  const paginatedLoncheados = useMemo(() => {
    const total = sortedLoncheados?.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    return {
      data: sortedLoncheados?.slice((currentPage - 1) * pageSize, currentPage * pageSize),
      total, pages
    };
  }, [sortedLoncheados, currentPage, pageSize]);

  // ─── FILTER HANDLERS ─────────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({
        dateFrom: '', dateTo: '', clienteId: 'all', empleadoId: 'all',
        productoId: 'all', tipoCorte: 'all', rentabilidad: 'all',
        estadoCalculo: 'all', estadoRendimiento: 'all', quickDate: 'all'
      });
      setSearch('');
      setCurrentPage(1);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    }
  };

  const handleQuickDate = (preset) => {
    const range = getQuickDateRange(preset);
    if (range) {
      setFilters(prev => ({ ...prev, dateFrom: range?.dateFrom, dateTo: range?.dateTo, quickDate: preset }));
      setCurrentPage(1);
    }
  };

  // ─── CHART DATA ───────────────────────────────────────────────────────────
  const clienteBarData = clienteData?.slice(0, 8)?.map(c => ({
    name: c?.clienteNombre?.split(' ')?.[0] || c?.clienteNombre,
    facturacion: c?.facturacion,
    beneficio: c?.beneficio,
  }));

  const tipoCorteData = useMemo(() => {
    const map = {};
    filteredLoncheados?.forEach(l => {
      const tipo = l?.tipo || 'Sin tipo';
      if (!map?.[tipo]) map[tipo] = { name: tipo, value: 0, beneficio: 0 };
      if (l?.estado_calculo !== 'SIN_TARIFA' && l?.ingreso) map[tipo].value += l?.ingreso;
      if (l?.beneficio != null) map[tipo].beneficio += l?.beneficio;
    });
    return Object.values(map);
  }, [filteredLoncheados]);

  const tiposCorte = useMemo(() => {
    const s = new Set(loncheados?.map(l => l?.tipo)?.filter(Boolean));
    return Array.from(s);
  }, [loncheados]);

  const hasActiveFilters = filters?.dateFrom || filters?.dateTo || filters?.clienteId !== 'all' ||
    filters?.empleadoId !== 'all' || filters?.productoId !== 'all' || filters?.tipoCorte !== 'all' ||
    filters?.rentabilidad !== 'all' || filters?.estadoCalculo !== 'all' || filters?.estadoRendimiento !== 'all' || search?.trim();

  const QUICK_DATE_PRESETS = [
    { id: 'today', label: 'Hoy' },
    { id: 'week', label: 'Esta semana' },
    { id: 'month', label: 'Este mes' },
    { id: 'prev_month', label: 'Mes anterior' },
    { id: 'quarter', label: 'Este trimestre' },
    { id: 'year', label: 'Este año' },
    { id: 'all', label: 'Todo' },
  ];

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet><title>Rentabilidad Sala — Las Dos Doncellas</title></Helmet>
      <ERPPage>
        <ERPPageHeader
          title="Rentabilidad Sala"
          subtitle="Ingreso = tarifa del SERVICIO (por pieza, según casuística). Costes = cortador + envases. Sin ldd_rentabilidad_control."
          actions={null}
        />

        {/* ── AUDITORÍA VISUAL DE DATOS ── */}
        {!loading && (
          <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-2.5 rounded-xl bg-[#111111] border border-white/6 text-xs font-oranienbaum text-white/40">
            <span className="text-white/60 font-semibold">Auditoría:</span>
            <span>Registros analizados: <strong className="text-white/70">{auditStats?.total}</strong></span>
            <span className="text-white/20">·</span>
            <span>Con tarifa: <strong className="text-[#3A7F6B]">{auditStats?.conTarifa}</strong></span>
            <span className="text-white/20">·</span>
            <span>Sin tarifa: <strong className="text-[#E05252]">{auditStats?.sinTarifa}</strong></span>
            <span className="text-white/20">·</span>
            <span>Provisionales: <strong className="text-[#C9A24D]">{auditStats?.provisionales}</strong></span>
            <span className="text-white/20">·</span>
            <span>Pesos inconsistentes: <strong className="text-[#E05252]">{auditStats?.inconsistentes}</strong></span>
            {auditStats?.rendimientosNoCoincidentes > 0 && (
              <>
                <span className="text-white/20">·</span>
                <span className="text-[#C9A24D]">⚠ Rendimientos no coincidentes: <strong>{auditStats?.rendimientosNoCoincidentes}</strong></span>
              </>
            )}
            {auditStats?.duplicados > 0 && (
              <>
                <span className="text-white/20">·</span>
                <span className="text-[#E05252]">⚠ Duplicados: <strong>{auditStats?.duplicados}</strong></span>
              </>
            )}
          </div>
        )}

        {/* ── LEVEL SELECTOR ── */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {[
            { id: 'sala', label: '📊 Sala Completa', desc: 'KPIs globales' },
            { id: 'cliente', label: '👤 Por Cliente', desc: 'Agrupado por cliente' },
            { id: 'loncheado', label: '🔪 Por Loncheado', desc: 'Detalle individual' },
            { id: 'control', label: '🔍 Control Cálculo', desc: 'Diagnóstico' },
            { id: 'reconciliacion', label: '⚖ Cuadre', desc: 'Detalle = Clientes = Sala' },
          ]?.map(level => (
            <button
              key={level?.id}
              onClick={() => setViewLevel(level?.id)}
              className={`flex flex-col items-start px-4 py-2.5 rounded-xl border transition-all ${
                viewLevel === level?.id
                  ? 'bg-[#D6EF4E]/12 border-[#D6EF4E]/30 text-[#D6EF4E]'
                  : 'bg-[#1A1A1A] border-white/8 text-white/60 hover:text-white hover:border-white/20'
              }`}>
              <span className="text-sm font-oranienbaum font-semibold">{level?.label}</span>
              <span className="text-xs font-oranienbaum opacity-60">{level?.desc}</span>
            </button>
          ))}
        </div>

        {/* ── QUICK DATE FILTERS ── */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {QUICK_DATE_PRESETS?.map(p => (
            <button
              key={p?.id}
              onClick={() => handleQuickDate(p?.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold border transition-all ${
                filters?.quickDate === p?.id
                  ? 'bg-[#D6EF4E]/15 border-[#D6EF4E]/30 text-[#D6EF4E]'
                  : 'bg-[#1A1A1A] border-white/8 text-white/50 hover:text-white/80 hover:border-white/20'
              }`}>
              {p?.label}
            </button>
          ))}
        </div>

        {/* ── FILTERS ── */}
        <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-3 mb-5 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <ERPSearchInput
              value={search}
              onChange={(v) => { setSearch(v); setCurrentPage(1); }}
              placeholder="Buscar cliente, pieza, lote, cortador, producto, formato..."
            />
            <ERPFilterSelect value={filters?.clienteId} onChange={(v) => handleFilterChange('clienteId', v)}>
              <option value="all">Todos los clientes</option>
              {clientes?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}</option>)}
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.empleadoId} onChange={(v) => handleFilterChange('empleadoId', v)}>
              <option value="all">Todos los cortadores</option>
              {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.productoId} onChange={(v) => handleFilterChange('productoId', v)}>
              <option value="all">Todos los productos</option>
              {productos?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
            </ERPFilterSelect>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ERPFilterSelect value={filters?.tipoCorte} onChange={(v) => handleFilterChange('tipoCorte', v)}>
              <option value="all">Tipo de corte: Todos</option>
              {tiposCorte?.map(t => <option key={t} value={t}>{t}</option>)}
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.rentabilidad} onChange={(v) => handleFilterChange('rentabilidad', v)}>
              <option value="all">Rentabilidad: Todos</option>
              <option value="rentable">🟢 Rentable (≥30%)</option>
              <option value="margen_bajo">🟡 Margen bajo (0-30%)</option>
              <option value="perdida">🔴 Pérdida</option>
              <option value="sin_tarifa">⚠ Sin tarifa</option>
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.estadoCalculo} onChange={(v) => handleFilterChange('estadoCalculo', v)}>
              <option value="all">Estado cálculo: Todos</option>
              <option value="completo">✓ Completo</option>
              <option value="provisional">⚠ Provisional</option>
              <option value="sin_tarifa">Sin tarifa</option>
              <option value="sin_datos">✗ Datos incompletos</option>
            </ERPFilterSelect>
            <ERPFilterSelect value={filters?.estadoRendimiento} onChange={(v) => handleFilterChange('estadoRendimiento', v)}>
              <option value="all">Rendimiento: Todos</option>
              <option value="correcto">✓ Correcto</option>
              <option value="incompleto">— Incompleto</option>
              <option value="inconsistente">⚠ Inconsistente</option>
            </ERPFilterSelect>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs font-oranienbaum text-white/40">Desde:</span>
              <input type="date" value={filters?.dateFrom} onChange={(e) => { handleFilterChange('dateFrom', e?.target?.value); handleFilterChange('quickDate', 'custom'); }}
                className="bg-[#111111] border border-white/10 text-white/70 text-xs rounded-lg px-2 py-1.5 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40" />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs font-oranienbaum text-white/40">Hasta:</span>
              <input type="date" value={filters?.dateTo} onChange={(e) => { handleFilterChange('dateTo', e?.target?.value); handleFilterChange('quickDate', 'custom'); }}
                className="bg-[#111111] border border-white/10 text-white/70 text-xs rounded-lg px-2 py-1.5 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40" />
            </div>
            {hasActiveFilters && (
              <button
                onClick={() => handleFilterChange('reset', null)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold bg-[#E05252]/10 border border-[#E05252]/20 text-[#E05252] hover:bg-[#E05252]/20 transition-colors">
                <Icon name="X" size={12} />
                Limpiar filtros
              </button>
            )}
            <span className="text-xs font-oranienbaum text-white/30 ml-auto">
              {filteredLoncheados?.length} resultado{filteredLoncheados?.length !== 1 ? 's' : ''} encontrado{filteredLoncheados?.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {error && <div className="mb-4"><ERPError message={error} onRetry={loadAll} /></div>}

        {/* ══════════════════════════════════════════════════════════════════
            NIVEL 1: SALA COMPLETA
        ══════════════════════════════════════════════════════════════════ */}
        {viewLevel === 'sala' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <ERPKPICard title="Nº Piezas / Loncheados" value={salaKPIs?.nPiezas} subtitle="Total periodo" icon="Hash" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={loading} trend={null} trendLabel="" />
              <ERPKPICard title="Kg Iniciales" value={salaKPIs?.kgIniciales > 0 ? formatKg(salaKPIs?.kgIniciales) : 'Sin datos'} subtitle="Peso entrada (peso_inicial)" icon="Weight" iconColor="#8097D6" valueColor="#8097D6" loading={loading} trend={null} trendLabel="" />
              <ERPKPICard title="Kg Aprovechados" value={salaKPIs?.kgAprovechados > 0 ? formatKg(salaKPIs?.kgAprovechados) : 'Sin datos'} subtitle="Peso salida (peso_aprovechado)" icon="CheckCircle" iconColor="#3A7F6B" valueColor="#3A7F6B" loading={loading} trend={null} trendLabel="" />
              <ERPKPICard
                title="Rendimiento Medio"
                value={salaKPIs?.rendimientoMedio !== null ? formatPct(salaKPIs?.rendimientoMedio) : 'Sin datos'}
                subtitle={salaKPIs?.nSinRendimiento > 0 ? `${salaKPIs?.nSinRendimiento} registros excluidos` : 'Aprovechamiento ponderado'}
                icon="Percent"
                iconColor={salaKPIs?.rendimientoMedio !== null ? getMargenColor(salaKPIs?.rendimientoMedio) : '#6B7280'}
                valueColor={salaKPIs?.rendimientoMedio !== null ? getMargenColor(salaKPIs?.rendimientoMedio) : '#6B7280'}
                loading={loading} trend={null} trendLabel=""
              />
              <ERPKPICard
                title="Facturación Servicios"
                value={formatCurrency(salaKPIs?.facturacion)}
                subtitle={salaKPIs?.nSinTarifa > 0 ? `${salaKPIs?.nSinTarifa} piezas sin tarifa excluidas` : 'Tarifa servicio por pieza'}
                icon="TrendingUp" iconColor="#3A7F6B" valueColor="#3A7F6B" loading={loading} trend={null} trendLabel=""
              />
              <ERPKPICard title="Coste Cortadores" value={salaKPIs?.costeCortadores > 0 ? formatCurrency(salaKPIs?.costeCortadores) : '0,00 €'} subtitle="Mano de obra cortador" icon="Users" iconColor="#E05252" valueColor={salaKPIs?.costeCortadores > 0 ? '#E05252' : '#6B7280'} loading={loading} trend={null} trendLabel="" />
              <ERPKPICard title="Coste Envases" value={salaKPIs?.costeEnvases > 0 ? formatCurrency(salaKPIs?.costeEnvases) : '0,00 €'} subtitle="Plásticos / blísteres (0,07€/ud)" icon="Package" iconColor="#E05252" valueColor={salaKPIs?.costeEnvases > 0 ? '#E05252' : '#6B7280'} loading={loading} trend={null} trendLabel="" />
              <ERPKPICard
                title="Coste Conocido"
                value={salaKPIs?.costeTotal > 0 ? formatCurrency(salaKPIs?.costeTotal) : '0,00 €'}
                subtitle={salaKPIs?.nPendienteEnvasador > 0 ? `${salaKPIs?.nPendienteEnvasador} piezas sin coste envasador` : 'Cortador + envases'}
                icon="Receipt" iconColor="#E05252" valueColor={salaKPIs?.costeTotal > 0 ? '#E05252' : '#6B7280'} loading={loading} trend={null} trendLabel=""
              />
              <ERPKPICard
                title="Beneficio Provisional"
                value={formatCurrency(salaKPIs?.beneficio)}
                subtitle={salaKPIs?.nProvisionales > 0 ? `${salaKPIs?.nProvisionales} piezas provisionales` : salaKPIs?.margen !== null ? `Margen: ${formatPct(salaKPIs?.margen)}` : 'Sin datos de coste'}
                icon="DollarSign" iconColor={salaKPIs?.beneficio >= 0 ? '#3A7F6B' : '#E05252'} valueColor={salaKPIs?.beneficio >= 0 ? '#3A7F6B' : '#E05252'} loading={loading} trend={null} trendLabel=""
              />
            </div>

            {/* Nota aclaratoria sobre provisional */}
            {salaKPIs?.nPendienteEnvasador > 0 && (
              <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-[#C9A24D]/8 border border-[#C9A24D]/20">
                <Icon name="Info" size={14} className="text-[#C9A24D] mt-0.5 flex-shrink-0" />
                <p className="text-xs font-oranienbaum text-[#C9A24D]/80">
                  <strong>Coste Conocido</strong> y <strong>Beneficio Provisional</strong>: {salaKPIs?.nPendienteEnvasador} pieza{salaKPIs?.nPendienteEnvasador > 1 ? 's' : ''} con coste de envasador pendiente de configurar. Los importes mostrados son <strong>PROVISIONALES</strong> (ingreso − cortador − envases). Cuando se configure el coste del envasador pasarán a COMPLETO.
                  {salaKPIs?.nTupper > 0 && ` ${salaKPIs?.nTupper} pieza${salaKPIs?.nTupper > 1 ? 's' : ''} TUPPER_CON_GRASA tienen coste envasador = 0 y pueden calcularse de forma definitiva.`}
                </p>
              </div>
            )}

            {salaKPIs?.margen !== null && (
              <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border" style={{
                background: salaKPIs?.margen >= 30 ? 'rgba(58,127,107,0.08)' : salaKPIs?.margen >= 0 ? 'rgba(201,162,77,0.08)' : 'rgba(224,82,82,0.08)',
                borderColor: salaKPIs?.margen >= 30 ? 'rgba(58,127,107,0.25)' : salaKPIs?.margen >= 0 ? 'rgba(201,162,77,0.25)' : 'rgba(224,82,82,0.25)',
              }}>
                <RentabilidadBadge margen={salaKPIs?.margen} provisional={salaKPIs?.nProvisionales > 0} />
                <span className="text-sm font-oranienbaum text-white/70">
                  Margen global de la sala: <strong style={{ color: getMargenColor(salaKPIs?.margen) }}>{formatPct(salaKPIs?.margen)}</strong>
                </span>
              </div>
            )}

            {(salaKPIs?.nSinTarifa > 0 || salaKPIs?.nInconsistentes > 0) && (
              <div className="mb-5 flex items-start gap-2 px-4 py-3 rounded-xl bg-[#E05252]/8 border border-[#E05252]/20">
                <Icon name="AlertTriangle" size={14} className="text-[#E05252] mt-0.5 flex-shrink-0" />
                <p className="text-xs font-oranienbaum text-[#E05252]/80">
                  {salaKPIs?.nSinTarifa > 0 && `${salaKPIs?.nSinTarifa} pieza${salaKPIs?.nSinTarifa > 1 ? 's' : ''} sin tarifa de servicio — excluidas de la facturación. `}
                  {salaKPIs?.nInconsistentes > 0 && `${salaKPIs?.nInconsistentes} registro${salaKPIs?.nInconsistentes > 1 ? 's' : ''} con pesos inconsistentes excluidos del rendimiento.`}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
              <ERPCard title="Facturación vs Beneficio por Cliente" titleAction={null}>
                {loading ? <ERPLoading /> : clienteBarData?.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={clienteBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Oranienbaum' }} tickFormatter={(v) => `€${(v / 1000)?.toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                      <Bar dataKey="facturacion" name="Facturación" fill="#3A7F6B" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="beneficio" name="Beneficio" fill="#D6EF4E" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <ERPEmpty message="Sin datos" />}
              </ERPCard>

              <ERPCard title="Distribución por Tipo de Corte" titleAction={null}>
                {loading ? <ERPLoading /> : tipoCorteData?.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={tipoCorteData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" nameKey="name">
                        {tipoCorteData?.map((_, i) => <Cell key={i} fill={CHART_COLORS?.[i % CHART_COLORS?.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontFamily: 'Oranienbaum', fontSize: 10, color: 'rgba(255,255,255,0.5)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <ERPEmpty message="Sin datos de tipo de corte" />}
              </ERPCard>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            NIVEL 2: POR CLIENTE
        ══════════════════════════════════════════════════════════════════ */}
        {viewLevel === 'cliente' && (
          <>
            {loading ? <ERPLoading /> : !clienteData?.length ? <ERPEmpty message="No hay datos para los filtros seleccionados" /> : (
              <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                  <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E]">Rentabilidad por Cliente</h2>
                  <span className="text-xs font-oranienbaum text-white/40">{clienteData?.length} clientes</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-oranienbaum">
                    <thead>
                      <tr className="border-b border-white/8 bg-[#141414]">
                        {['#', 'Cliente', 'Piezas', 'Kg Iniciales', 'Kg Aprovechados', 'Rendimiento', 'Facturación', 'Coste Cortador', 'Coste Envases', 'Coste Conocido', 'Benef. Provisional', 'Margen', 'Estado']?.map((h, i) => (
                          <th key={i} className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {clienteData?.map((c, idx) => (
                        <tr key={c?.clienteId} className="hover:bg-white/3 transition-colors cursor-pointer"
                          onClick={() => { setFilters(prev => ({ ...prev, clienteId: c?.clienteId })); setViewLevel('loncheado'); }}>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              idx === 0 ? 'bg-[#D6EF4E]/20 text-[#D6EF4E]' : idx === 1 ? 'bg-white/10 text-white/60' : idx === 2 ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'bg-white/5 text-white/40'
                            }`}>{idx + 1}</span>
                          </td>
                          <td className="px-3 py-2.5 font-semibold text-white/90 whitespace-nowrap">{c?.clienteNombre}</td>
                          <td className="px-3 py-2.5 text-[#C9A24D] font-semibold">{c?.nPiezas}</td>
                          <td className="px-3 py-2.5 text-white/60">{c?.kgIniciales > 0 ? formatKg(c?.kgIniciales) : '—'}</td>
                          <td className="px-3 py-2.5 text-white/60">{c?.kgAprovechados > 0 ? formatKg(c?.kgAprovechados) : '—'}</td>
                          <td className="px-3 py-2.5">{c?.rendimientoMedio !== null ? <span style={{ color: getMargenColor(c?.rendimientoMedio) }}>{formatPct(c?.rendimientoMedio)}</span> : <span className="text-white/30">—</span>}</td>
                          <td className="px-3 py-2.5 font-semibold">
                            {c?.facturacion > 0
                              ? <span className="text-[#3A7F6B]">{formatCurrency(c?.facturacion)}</span>
                              : <span className="text-white/30">Sin registrar</span>}
                            {c?.nSinTarifa > 0 && <span className="block text-xs text-[#E05252]/60">{c?.nSinTarifa} sin tarifa</span>}
                          </td>
                          <td className="px-3 py-2.5">{c?.costeCortadores > 0 ? <span className="text-[#E05252]">{formatCurrency(c?.costeCortadores)}</span> : <span className="text-white/30">Sin registrar</span>}</td>
                          <td className="px-3 py-2.5">{c?.costeEnvases > 0 ? <span className="text-[#E05252]">{formatCurrency(c?.costeEnvases)}</span> : <span className="text-white/30">—</span>}</td>
                          <td className="px-3 py-2.5">{c?.costeTotal > 0 ? <span className="text-[#E05252]">{formatCurrency(c?.costeTotal)}</span> : <span className="text-white/30">Sin registrar</span>}</td>
                          <td className="px-3 py-2.5 font-bold" style={{ color: c?.beneficio >= 0 ? '#3A7F6B' : '#E05252' }}>{formatCurrency(c?.beneficio)}</td>
                          <td className="px-3 py-2.5">{c?.margen !== null ? <span style={{ color: getMargenColor(c?.margen) }}>{formatPct(c?.margen)}</span> : <span className="text-white/30">—</span>}</td>
                          <td className="px-3 py-2.5"><RentabilidadBadge margen={c?.margen} provisional={c?.nProvisionales > 0} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[#D6EF4E]/20 bg-[#141414]">
                        <td colSpan={2} className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#D6EF4E]">TOTAL SALA</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#C9A24D]">{salaKPIs?.nPiezas}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-white/60">{formatKg(salaKPIs?.kgIniciales)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-white/60">{formatKg(salaKPIs?.kgAprovechados)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum" style={{ color: salaKPIs?.rendimientoMedio !== null ? getMargenColor(salaKPIs?.rendimientoMedio) : '#6B7280' }}>{salaKPIs?.rendimientoMedio !== null ? formatPct(salaKPIs?.rendimientoMedio) : '—'}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#3A7F6B]">{formatCurrency(salaKPIs?.facturacion)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#E05252]">{formatCurrency(salaKPIs?.costeCortadores)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#E05252]">{formatCurrency(salaKPIs?.costeEnvases)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum text-[#E05252]">{formatCurrency(salaKPIs?.costeTotal)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum" style={{ color: salaKPIs?.beneficio >= 0 ? '#3A7F6B' : '#E05252' }}>{formatCurrency(salaKPIs?.beneficio)}</td>
                        <td className="px-3 py-2.5 text-xs font-bold font-oranienbaum" style={{ color: salaKPIs?.margen !== null ? getMargenColor(salaKPIs?.margen) : '#6B7280' }}>{salaKPIs?.margen !== null ? formatPct(salaKPIs?.margen) : '—'}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            NIVEL 3: POR LONCHEADO INDIVIDUAL
        ══════════════════════════════════════════════════════════════════ */}
        {viewLevel === 'loncheado' && (
          <>
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E]">Rentabilidad por Loncheado</h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-oranienbaum text-white/40">
                    Mostrando {paginatedLoncheados?.total > 0 ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, paginatedLoncheados?.total)}` : '0'} de {paginatedLoncheados?.total?.toLocaleString('es-ES')}
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                    className="bg-[#111111] border border-white/10 text-white/60 text-xs rounded-lg px-2 py-1 font-oranienbaum focus:outline-none">
                    <option value={25}>25 / pág.</option>
                    <option value={50}>50 / pág.</option>
                    <option value={100}>100 / pág.</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-oranienbaum">
                  <thead>
                    <tr className="border-b border-white/8 bg-[#141414]">
                      <SortableTh field="fecha" label="Fecha" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="cliente" label="Cliente" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Producto</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Cortador</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Tipo Corte</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Formato</th>
                      <SortableTh field="peso_inicial" label="Peso Inicial" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="peso_aprovechado" label="Peso Aprov." sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="rendimiento" label="Rendim." sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Blísteres</th>
                      <SortableTh field="ingreso" label="Ingreso Servicio" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Coste Cortador</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Coste Envases</th>
                      <SortableTh field="coste_total" label="Coste Conocido" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="beneficio" label="Benef. Prov." sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="margen" label="Margen" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">Estado</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {loading ? (
                      <tr><td colSpan={17} className="py-16"><ERPLoading /></td></tr>
                    ) : !paginatedLoncheados?.data?.length ? (
                      <tr><td colSpan={17} className="py-16"><ERPEmpty message="No hay loncheados para los filtros seleccionados" /></td></tr>
                    ) : paginatedLoncheados?.data?.map((l) => (
                      <tr key={l?.id} className={`hover:bg-white/3 transition-colors ${l?.pesos_inconsistentes ? 'bg-[#E05252]/3' : ''}`}>
                        <td className="px-3 py-2.5 text-white/60 whitespace-nowrap">{formatDate(l?.fecha_produccion || l?.created_at)}</td>
                        <td className="px-3 py-2.5 font-semibold text-white/90 whitespace-nowrap">{l?.cliente_nombre}</td>
                        <td className="px-3 py-2.5 text-white/70 whitespace-nowrap">{l?.producto_nombre}</td>
                        <td className="px-3 py-2.5 text-white/60 whitespace-nowrap">{l?.empleado_nombre}</td>
                        <td className="px-3 py-2.5 text-white/50">{l?.tipo || '—'}</td>
                        <td className="px-3 py-2.5">
                          {l?.formato_salida
                            ? <span className="text-xs px-1.5 py-0.5 rounded bg-[#8097D6]/10 text-[#8097D6] border border-[#8097D6]/20">{l?.formato_salida}</span>
                            : <span className="text-white/25 text-xs">HISTÓRICO</span>}
                        </td>
                        <td className="px-3 py-2.5 text-white/60">{l?.peso_inicial_calc > 0 ? formatKg(l?.peso_inicial_calc) : <span className="text-white/25">—</span>}</td>
                        <td className="px-3 py-2.5">
                          {l?.pesos_inconsistentes
                            ? <PesoInconsistenteBadge />
                            : l?.peso_aprovechado_calc !== null
                              ? <span className="text-white/60">{formatKg(l?.peso_aprovechado_calc)}</span>
                              : <span className="text-white/25">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {l?.pesos_inconsistentes
                            ? <span className="text-white/25 text-xs">—</span>
                            : l?.rendimiento_calc !== null
                              ? <span style={{ color: getMargenColor(l?.rendimiento_calc) }}>{formatPct(l?.rendimiento_calc)}</span>
                              : <span className="text-white/25">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-white/60">
                          {l?.numero_blisteres_final != null
                            ? <span>{l?.numero_blisteres_final}{l?.numero_blisteres_calculado != null && (l?.numero_blisteres == null || l?.numero_blisteres === 0) ? <span className="text-white/30 text-xs ml-1">(calc.)</span> : null}</span>
                            : l?.es_tupper
                              ? <span className="text-white/25 text-xs">N/A</span>
                              : <span className="text-white/25">—</span>}
                        </td>
                        <td className="px-3 py-2.5 font-semibold">
                          {l?.ingreso != null && l?.ingreso > 0
                            ? <span className="text-[#3A7F6B]">{formatCurrency(l?.ingreso)}</span>
                            : <span className="text-[#E05252]/70 text-xs">Sin tarifa</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {l?.coste_cortador != null && l?.coste_cortador > 0
                            ? <span className="text-[#E05252]">{formatCurrency(l?.coste_cortador)}</span>
                            : <span className="text-white/25 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {l?.coste_envases != null && l?.coste_envases > 0
                            ? <span className="text-[#E05252]">{formatCurrency(l?.coste_envases)}</span>
                            : <span className="text-white/25 text-xs">—</span>}
                        </td>
                        {/* Coste Conocido — diferenciado de coste definitivo */}
                        <td className="px-3 py-2.5">
                          {l?.coste_total != null && l?.coste_total > 0
                            ? <span className="text-[#E05252]">{formatCurrency(l?.coste_total)}{l?.necesita_envasador ? <span className="text-white/30 text-xs ml-1">*</span> : null}</span>
                            : <span className="text-white/25 text-xs">—</span>}
                        </td>
                        {/* Beneficio Provisional */}
                        <td className="px-3 py-2.5 font-bold">
                          {l?.beneficio != null
                            ? <span style={{ color: l?.beneficio >= 0 ? '#3A7F6B' : '#E05252' }}>{formatCurrency(l?.beneficio)}{l?.necesita_envasador ? <span className="text-white/30 text-xs ml-1">*</span> : null}</span>
                            : <span className="text-white/25 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {l?.margen !== null
                            ? <span style={{ color: getMargenColor(l?.margen) }}>{formatPct(l?.margen)}</span>
                            : <span className="text-white/25">—</span>}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {l?.pesos_inconsistentes
                            ? <PesoInconsistenteBadge />
                            : l?.estado_calculo === 'SIN_TARIFA'
                              ? <EstadoCalculoBadge estado="SIN_TARIFA" />
                              : l?.estado_calculo !== 'SIN_DATOS'
                                ? <RentabilidadBadge margen={l?.margen} provisional={l?.estado_calculo === 'PROVISIONAL'} />
                                : <EstadoCalculoBadge estado="SIN_DATOS" />}
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => setSelectedLoncheado(l)}
                            className="text-xs font-oranienbaum text-[#D6EF4E]/70 hover:text-[#D6EF4E] transition-colors px-2 py-1 rounded bg-[#D6EF4E]/5 hover:bg-[#D6EF4E]/10 whitespace-nowrap">
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Leyenda provisional */}
              {salaKPIs?.nPendienteEnvasador > 0 && (
                <div className="px-4 py-2 border-t border-white/5 text-xs font-oranienbaum text-white/30">
                  * Coste Conocido y Beneficio Provisional — PROVISIONAL: falta coste envasador. Los valores definitivos se calcularán cuando se configure el coste/hora del envasador.
                </div>
              )}
              {paginatedLoncheados?.total > 0 && (
                <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs font-oranienbaum text-white/40">
                    Mostrando {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, paginatedLoncheados?.total)} de {paginatedLoncheados?.total?.toLocaleString('es-ES')}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                      className="px-2 py-1.5 rounded-lg text-xs font-oranienbaum bg-[#111111] border border-white/8 text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">«</button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum bg-[#111111] border border-white/8 text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">‹ Anterior</button>
                    <span className="px-3 py-1.5 text-xs font-oranienbaum text-white/60">Página {currentPage} de {paginatedLoncheados?.pages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(paginatedLoncheados?.pages, p + 1))} disabled={currentPage === paginatedLoncheados?.pages}
                      className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum bg-[#111111] border border-white/8 text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Siguiente ›</button>
                    <button onClick={() => setCurrentPage(paginatedLoncheados?.pages)} disabled={currentPage === paginatedLoncheados?.pages}
                      className="px-2 py-1.5 rounded-lg text-xs font-oranienbaum bg-[#111111] border border-white/8 text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">»</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            NIVEL 4: CONTROL DE CÁLCULO (DIAGNÓSTICO)
        ══════════════════════════════════════════════════════════════════ */}
        {viewLevel === 'control' && (
          <div className="space-y-4">
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-5">
              <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E] mb-1">🔍 Control de Cálculo — Diagnóstico Rentabilidad Sala</h2>
              <p className="text-xs font-oranienbaum text-white/40 mb-5">
                Todos los importes calculados exclusivamente desde tarifas de servicio + costes de Sala.
                <strong className="text-[#E05252]"> ldd_rentabilidad_control NO se usa en Sala.</strong>
              </p>

              {/* 16 métricas de diagnóstico */}
              <h3 className="text-xs font-oranienbaum font-bold text-white/50 uppercase tracking-wider mb-3">📋 Resultados de Diagnóstico (16 métricas)</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {[
                  { n: 1, label: 'Facturación total Sala', value: formatCurrency(salaKPIs?.facturacion), color: '#3A7F6B' },
                  { n: 2, label: 'Nº total de piezas', value: salaKPIs?.nPiezas, color: '#D6EF4E' },
                  { n: 3, label: 'Piezas con tarifa válida', value: salaKPIs?.nConTarifa, color: '#3A7F6B' },
                  { n: 4, label: 'Piezas sin tarifa', value: salaKPIs?.nSinTarifa, color: '#E05252' },
                  { n: 5, label: 'Piezas 100G', value: salaKPIs?.nCon100G, color: '#8097D6' },
                  { n: 6, label: 'Piezas 500G', value: enrichedLoncheados?.filter(l => (l?.formato_salida || '')?.toUpperCase() === '500G')?.length, color: '#8097D6' },
                  { n: 7, label: 'Piezas Tupper', value: salaKPIs?.nTupper, color: '#C9A24D' },
                  { n: 8, label: 'Piezas sin formato', value: salaKPIs?.nSinFormato, color: '#E05252' },
                  { n: 9, label: 'Total blísteres reales (BD)', value: salaKPIs?.totalBlistersReales, color: '#8097D6' },
                  { n: 10, label: 'Total blísteres calculados', value: salaKPIs?.totalBlistersCalculados, color: '#C9A24D' },
                  { n: 11, label: 'Total blísteres usados', value: salaKPIs?.totalBlistersUsados, color: '#D6EF4E' },
                  { n: 12, label: 'Coste total plásticos', value: formatCurrency(salaKPIs?.costeEnvases), color: '#E05252' },
                  { n: 13, label: 'Piezas pendientes envasador', value: salaKPIs?.nPendienteEnvasador, color: '#C9A24D' },
                  { n: 14, label: 'Ingreso medio por pieza', value: salaKPIs?.ingresoMedio != null ? formatCurrency(salaKPIs?.ingresoMedio) : '—', color: '#3A7F6B' },
                  { n: 15, label: 'Diferencia Detalle vs Clientes', value: formatCurrency(reconciliacion?.diff_detalle_clientes?.ingreso), color: reconciliacion?.diff_detalle_clientes?.ingreso < 0.01 ? '#3A7F6B' : '#E05252' },
                  { n: 16, label: 'Diferencia Clientes vs Sala', value: formatCurrency(reconciliacion?.diff_clientes_sala?.ingreso), color: reconciliacion?.diff_clientes_sala?.ingreso < 0.01 ? '#3A7F6B' : '#E05252' },
                ]?.map((item) => (
                  <div key={item?.n} className="bg-[#111111] rounded-lg border border-white/6 px-4 py-3">
                    <p className="text-xs font-oranienbaum text-white/30 mb-0.5">{item?.n}. {item?.label}</p>
                    <p className="text-base font-bold font-oranienbaum" style={{ color: item?.color }}>{item?.value}</p>
                  </div>
                ))}
              </div>

              {/* Validación precio_cliente */}
              <h3 className="text-xs font-oranienbaum font-bold text-white/50 uppercase tracking-wider mb-3">🔎 Validación precio_cliente</h3>
              <div className="bg-[#111111] rounded-xl border border-white/6 p-4 mb-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Registros con precio_cliente &gt; 0</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#C9A24D]">{diagnostico?.conPrecioCliente ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Mínimo</p>
                    <p className="text-lg font-bold font-oranienbaum text-white/70">{diagnostico?.pcMin != null ? formatCurrency(diagnostico?.pcMin) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Máximo</p>
                    <p className="text-lg font-bold font-oranienbaum text-white/70">{diagnostico?.pcMax != null ? formatCurrency(diagnostico?.pcMax) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Media</p>
                    <p className="text-lg font-bold font-oranienbaum text-white/70">{diagnostico?.pcMedia != null ? formatCurrency(diagnostico?.pcMedia) : '—'}</p>
                  </div>
                </div>
                {diagnostico?.top20PrecioCliente?.length > 0 && (
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40 mb-2">Top 20 valores más altos de precio_cliente:</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs font-oranienbaum">
                        <thead>
                          <tr className="border-b border-white/8">
                            <th className="px-2 py-1.5 text-left text-white/40">Cliente</th>
                            <th className="px-2 py-1.5 text-left text-white/40">precio_cliente</th>
                            <th className="px-2 py-1.5 text-left text-white/40">Tipo</th>
                            <th className="px-2 py-1.5 text-left text-white/40">Peso inicial</th>
                            <th className="px-2 py-1.5 text-left text-white/40">Tarifa casuística</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {diagnostico?.top20PrecioCliente?.map((l, i) => {
                            const tarifas = clientesTarifas?.[l?.cliente_id];
                            const esEmplatado = l?.emplatado === true || l?.tipo === 'EMPLATADO' || l?.tipo === 'GRANDE_Y_EMPLATADO';
                            const peso = parseFloat(l?.peso_inicial) || 0;
                            let tarifaCasuistica = null;
                            if (l?.tipo === 'TUPPER_CON_GRASA') tarifaCasuistica = TUPPER_PRECIO_SERVICIO;
                            else if (esEmplatado && tarifas?.tarifa_emplatado) tarifaCasuistica = tarifas?.tarifa_emplatado;
                            else if (!esEmplatado && peso < 10 && tarifas?.tarifa_sin_emplatado_menor) tarifaCasuistica = tarifas?.tarifa_sin_emplatado_menor;
                            else if (!esEmplatado && peso >= 10 && tarifas?.tarifa_sin_emplatado_mayor) tarifaCasuistica = tarifas?.tarifa_sin_emplatado_mayor;
                            const pc = parseFloat(l?.precio_cliente);
                            const diferencia = tarifaCasuistica != null ? Math.abs(pc - tarifaCasuistica) : null;
                            const compatible = diferencia != null && diferencia < 1;
                            return (
                              <tr key={i} className="hover:bg-white/3">
                                <td className="px-2 py-1.5 text-white/70">{l?.cliente_nombre}</td>
                                <td className="px-2 py-1.5 font-bold" style={{ color: compatible ? '#3A7F6B' : '#C9A24D' }}>{formatCurrency(pc)}</td>
                                <td className="px-2 py-1.5 text-white/50">{l?.tipo || '—'}</td>
                                <td className="px-2 py-1.5 text-white/50">{l?.peso_inicial ? `${l?.peso_inicial} kg` : '—'}</td>
                                <td className="px-2 py-1.5">
                                  {tarifaCasuistica != null
                                    ? <span style={{ color: compatible ? '#3A7F6B' : '#C9A24D' }}>{formatCurrency(tarifaCasuistica)}{compatible ? ' ✓' : ` (dif. ${formatCurrency(diferencia)})`}</span>
                                    : <span className="text-white/30">Sin tarifa</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="mt-3 px-3 py-2 rounded-lg bg-[#C9A24D]/8 border border-[#C9A24D]/20">
                  <p className="text-xs font-oranienbaum text-[#C9A24D]/80">
                    ℹ precio_cliente ya NO se usa como fuente de ingreso. La facturación se calcula exclusivamente desde public.clientes (tarifa_emplatado / tarifa_sin_emplatado_menor / tarifa_sin_emplatado_mayor). Esta tabla es solo informativa.
                  </p>
                </div>
              </div>

              {/* Validación formato_corte */}
              <h3 className="text-xs font-oranienbaum font-bold text-white/50 uppercase tracking-wider mb-3">📐 Validación formato_corte (relaciones reales)</h3>
              <div className="bg-[#111111] rounded-xl border border-white/6 p-4 mb-5">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Total loncheados</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#D6EF4E]">{enrichedLoncheados?.length}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Desde loncheados.formato_corte ✅</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#D6EF4E]">{diagnostico?.formatoDesde_loncheados ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Con plan_trabajo_pieza_id</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#8097D6]">{diagnostico?.conPlanTrabajoPiezaId ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Formato desde plan_trabajo_piezas</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#3A7F6B]">{diagnostico?.formatoDesde_pieza ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Con plan_trabajo_id</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#8097D6]">{diagnostico?.conPlanTrabajoId ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Formato desde plan_trabajo</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#3A7F6B]">{diagnostico?.formatoDesde_plan ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Inferidos (blísteres/tipo)</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#C9A24D]">{diagnostico?.formatoInferido ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Fallback histórico 100G 📜</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#C9A24D]">{diagnostico?.formatoFallbackHistorico ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Sin formato (todos los fallbacks)</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#E05252]">{auditStats?.sinFormato}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Con formato 100G</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#8097D6]">{auditStats?.con100G}</p>
                  </div>
                  <div>
                    <p className="text-xs font-oranienbaum text-white/40">Con formato 500G</p>
                    <p className="text-lg font-bold font-oranienbaum text-[#8097D6]">{auditStats?.con500G}</p>
                  </div>
                </div>
                <div className="mt-3 px-3 py-2 rounded-lg bg-[#8097D6]/8 border border-[#8097D6]/20">
                  <p className="text-xs font-oranienbaum text-[#8097D6]/80">
                    Prioridad: loncheados.formato_corte → plan_trabajo_piezas → plan_trabajo → inferido → fallback histórico 100G (registros NORMAL con peso_aprovechado &gt; 0, solo cálculo, no se escribe en BD). Ejemplo: 4,60 kg → 46 blísteres → 3,22 € · 4,50 kg → 45 blísteres → 3,15 €
                  </p>
                </div>
              </div>

              {/* Separación LDD vs Sala */}
              <div className="bg-[#1A1A1A] rounded-xl border border-[#D6EF4E]/20 p-5">
                <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E] mb-3">📊 Separación SALA vs LDD Rentabilidad-Control</h3>
                <div className="space-y-2 text-xs font-oranienbaum">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-white/50">Facturación Sala (tarifa servicio, sin ldd_rentabilidad_control)</span>
                    <span className="text-[#3A7F6B] font-bold">{formatCurrency(salaKPIs?.facturacion)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-white/50">Coste cortadores (salarios reales + casuística)</span>
                    <span className="text-[#E05252] font-bold">{formatCurrency(salaKPIs?.costeCortadores)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-white/50">Coste envases (0,07€/blíster 100G · 2€ 500G)</span>
                    <span className="text-[#E05252] font-bold">{formatCurrency(salaKPIs?.costeEnvases)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-white/50">Coste envasador (pendiente de configurar)</span>
                    <span className="text-[#C9A24D] font-bold">Pendiente</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-white/50">Beneficio provisional Sala</span>
                    <span className="font-bold" style={{ color: salaKPIs?.beneficio >= 0 ? '#3A7F6B' : '#E05252' }}>{formatCurrency(salaKPIs?.beneficio)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-white/50">Margen provisional Sala</span>
                    <span className="font-bold" style={{ color: salaKPIs?.margen !== null ? getMargenColor(salaKPIs?.margen) : '#6B7280' }}>{salaKPIs?.margen !== null ? formatPct(salaKPIs?.margen) : '—'}</span>
                  </div>
                </div>
                <div className="mt-4 px-3 py-2 rounded-lg bg-[#3A7F6B]/8 border border-[#3A7F6B]/20">
                  <p className="text-xs font-oranienbaum text-[#3A7F6B]/80">
                    ✓ TOTAL DETALLE = TOTAL CLIENTES = TOTAL SALA — misma fuente (enrichedLoncheados filtrados).
                    ldd_rentabilidad_control sigue disponible en su módulo propio (LDD Rentabilidad-Control) sin modificaciones.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            NIVEL 5: CUADRE NUMÉRICO — DETALLE = CLIENTES = SALA
        ══════════════════════════════════════════════════════════════════ */}
        {viewLevel === 'reconciliacion' && (
          <div className="space-y-4">
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-5">
              <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E] mb-1">⚖ Cuadre Numérico — DETALLE = CLIENTES = SALA</h2>
              <p className="text-xs font-oranienbaum text-white/40 mb-5">
                Verificación de que las tres vistas usan exactamente la misma fuente de datos. La diferencia esperada es 0,00 €.
              </p>

              {/* Tabla de cuadre */}
              <div className="overflow-x-auto mb-5">
                <table className="w-full text-sm font-oranienbaum">
                  <thead>
                    <tr className="border-b border-white/8 bg-[#141414]">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider">Concepto</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider">DETALLE (Σ filas)</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider">CLIENTES (Σ clientes)</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider">SALA (KPIs)</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider">Dif. Det-Cli</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-white/50 uppercase tracking-wider">Dif. Cli-Sala</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {[
                      {
                        label: 'Ingreso / Facturación',
                        det: reconciliacion?.detalle?.ingreso,
                        cli: reconciliacion?.clientes?.ingreso,
                        sala: reconciliacion?.sala?.ingreso,
                        diffDC: reconciliacion?.diff_detalle_clientes?.ingreso,
                        diffCS: reconciliacion?.diff_clientes_sala?.ingreso,
                        color: '#3A7F6B',
                      },
                      {
                        label: 'Coste Cortador',
                        det: reconciliacion?.detalle?.cortador,
                        cli: reconciliacion?.clientes?.cortador,
                        sala: reconciliacion?.sala?.cortador,
                        diffDC: reconciliacion?.diff_detalle_clientes?.cortador,
                        diffCS: reconciliacion?.diff_clientes_sala?.cortador,
                        color: '#E05252',
                      },
                      {
                        label: 'Coste Envases',
                        det: reconciliacion?.detalle?.envases,
                        cli: reconciliacion?.clientes?.envases,
                        sala: reconciliacion?.sala?.envases,
                        diffDC: reconciliacion?.diff_detalle_clientes?.envases,
                        diffCS: reconciliacion?.diff_clientes_sala?.envases,
                        color: '#E05252',
                      },
                      {
                        label: 'Coste Conocido',
                        det: reconciliacion?.detalle?.costeConocido,
                        cli: reconciliacion?.clientes?.costeConocido,
                        sala: reconciliacion?.sala?.costeConocido,
                        diffDC: reconciliacion?.diff_detalle_clientes?.costeConocido,
                        diffCS: reconciliacion?.diff_clientes_sala?.costeConocido,
                        color: '#E05252',
                      },
                      {
                        label: 'Beneficio Provisional',
                        det: reconciliacion?.detalle?.beneficio,
                        cli: reconciliacion?.clientes?.beneficio,
                        sala: reconciliacion?.sala?.beneficio,
                        diffDC: reconciliacion?.diff_detalle_clientes?.beneficio,
                        diffCS: reconciliacion?.diff_clientes_sala?.beneficio,
                        color: '#D6EF4E',
                      },
                    ]?.map((row, i) => {
                      const diffDCOk = row?.diffDC != null && row?.diffDC < 0.01;
                      const diffCSOk = row?.diffCS != null && row?.diffCS < 0.01;
                      return (
                        <tr key={i} className="hover:bg-white/3">
                          <td className="px-4 py-2.5 font-semibold text-white/80">{row?.label}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: row?.color }}>{formatCurrency(row?.det)}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: row?.color }}>{formatCurrency(row?.cli)}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: row?.color }}>{formatCurrency(row?.sala)}</td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`font-bold text-xs px-2 py-0.5 rounded-full ${diffDCOk ? 'bg-[#3A7F6B]/15 text-[#3A7F6B]' : 'bg-[#E05252]/15 text-[#E05252]'}`}>
                              {diffDCOk ? '✓ 0,00 €' : formatCurrency(row?.diffDC)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`font-bold text-xs px-2 py-0.5 rounded-full ${diffCSOk ? 'bg-[#3A7F6B]/15 text-[#3A7F6B]' : 'bg-[#E05252]/15 text-[#E05252]'}`}>
                              {diffCSOk ? '✓ 0,00 €' : formatCurrency(row?.diffCS)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Resumen DETALLE */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[
                  { title: '📋 DETALLE (Σ filas individuales)', data: reconciliacion?.detalle },
                  { title: '👤 CLIENTES (Σ por cliente)', data: reconciliacion?.clientes },
                  { title: '📊 SALA (KPIs superiores)', data: reconciliacion?.sala },
                ]?.map((section, i) => (
                  <div key={i} className="bg-[#111111] rounded-xl border border-white/6 p-4">
                    <h4 className="text-xs font-oranienbaum font-bold text-white/60 mb-3">{section?.title}</h4>
                    <div className="space-y-1.5 text-xs font-oranienbaum">
                      <div className="flex justify-between"><span className="text-white/40">SUM Ingreso</span><span className="text-[#3A7F6B] font-bold">{formatCurrency(section?.data?.ingreso)}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">SUM Coste Cortador</span><span className="text-[#E05252] font-bold">{formatCurrency(section?.data?.cortador)}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">SUM Coste Envases</span><span className="text-[#E05252] font-bold">{formatCurrency(section?.data?.envases)}</span></div>
                      <div className="flex justify-between border-t border-white/5 pt-1.5"><span className="text-white/40">SUM Coste Conocido</span><span className="text-[#E05252] font-bold">{formatCurrency(section?.data?.costeConocido)}</span></div>
                      <div className="flex justify-between border-t border-white/5 pt-1.5"><span className="text-white/40">SUM Beneficio Prov.</span><span className="font-bold" style={{ color: (section?.data?.beneficio || 0) >= 0 ? '#3A7F6B' : '#E05252' }}>{formatCurrency(section?.data?.beneficio)}</span></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Resultado del cuadre */}
              <div className="mt-4 px-4 py-3 rounded-xl border" style={{
                background: (reconciliacion?.diff_detalle_clientes?.ingreso < 0.01 && reconciliacion?.diff_clientes_sala?.ingreso < 0.01)
                  ? 'rgba(58,127,107,0.08)' : 'rgba(224,82,82,0.08)',
                borderColor: (reconciliacion?.diff_detalle_clientes?.ingreso < 0.01 && reconciliacion?.diff_clientes_sala?.ingreso < 0.01)
                  ? 'rgba(58,127,107,0.25)' : 'rgba(224,82,82,0.25)',
              }}>
                {(reconciliacion?.diff_detalle_clientes?.ingreso < 0.01 && reconciliacion?.diff_clientes_sala?.ingreso < 0.01) ? (
                  <p className="text-xs font-oranienbaum text-[#3A7F6B]">
                    ✓ <strong>CUADRE CORRECTO</strong> — DETALLE = CLIENTES = SALA. Diferencia: 0,00 €. Las tres vistas usan exactamente la misma fuente de datos (enrichedLoncheados filtrados).
                  </p>
                ) : (
                  <p className="text-xs font-oranienbaum text-[#E05252]">
                    ⚠ <strong>DIFERENCIA DETECTADA</strong> — Revisar la lógica de agregación. Det-Cli: {formatCurrency(reconciliacion?.diff_detalle_clientes?.ingreso)} · Cli-Sala: {formatCurrency(reconciliacion?.diff_clientes_sala?.ingreso)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedLoncheado && (
          <LoncheadoDetailModal
            loncheado={selectedLoncheado}
            onClose={() => setSelectedLoncheado(null)}
          />
        )}
      </ERPPage>
    </>
  );
};

export default ClientProfitabilityAnalysis;